/**
 * 创建 Blockly 工作区
 * @module features/blockly/createWorkspace
 */

import * as Blockly from 'blockly';
import 'blockly/blocks';
import {
  MQTT_RUNTIME_PASSWORD_PLACEHOLDER,
  isRuntimeMqttPasswordPlaceholder,
  normalizePossiblyEscapedXml,
} from '@/utils';
import { getToolboxXml } from './toolbox';
import { registerBlocks } from './registerBlocks';

const BLOCK_TYPE_ALIASES: Record<string, string> = {
  // Legacy/simple UI aliases that AI may output
  screen_create: 'yc_ui_screen_create',
  set_active_screen: 'yc_ui_set_active_screen',
  label_create: 'yc_ui_label_create',
  label_set_text: 'yc_ui_label_set_text',
  label_set_align: 'yc_ui_label_set_align',
  label_set_font: 'yc_ui_label_set_font',
  label_set_text_color: 'yc_ui_label_set_text_color',
  yc_ui_label_set_style_text_color: 'yc_ui_label_set_text_color',
  yc_ui_obj_set_style_text_color: 'yc_ui_label_set_text_color',
  yc_ui_set_style_text_color: 'yc_ui_label_set_text_color',
  label_set_bg_color: 'yc_ui_label_set_bg_color',
  yc_ui_label_set_style_bg_color: 'yc_ui_label_set_bg_color',
  yc_ui_obj_set_style_bg_color: 'yc_ui_label_set_bg_color',
  yc_ui_set_style_bg_color: 'yc_ui_label_set_bg_color',
  label_set_pos: 'yc_ui_label_set_pos',
  label_set_size: 'yc_ui_label_set_size',
  obj_center: 'yc_ui_obj_center',
  obj_set_scrollbar_mode: 'yc_ui_obj_set_scrollbar_mode',
  obj_add_flag: 'yc_ui_obj_add_flag',
  obj_clear_flag: 'yc_ui_obj_clear_flag',
  obj_has_flag: 'yc_ui_obj_has_flag',
  color_picker: 'colour_picker',
  // Common YM aliases that some models may hallucinate
  yc_sensor_temp_read: 'ym_th1_read_temperature',
  yc_sensor_temperature_read: 'ym_th1_read_temperature',
  yc_temp_read: 'ym_th1_read_temperature',
  sensor_temp_read: 'ym_th1_read_temperature',
  ym_sensor_temp_read: 'ym_th1_read_temperature',
  ym_th1_temperature_read: 'ym_th1_read_temperature',
  th1_read_temperature: 'ym_th1_read_temperature',
  yc_sensor_humidity_read: 'ym_th1_read_humidity',
  yc_humidity_read: 'ym_th1_read_humidity',
  sensor_humidity_read: 'ym_th1_read_humidity',
  ym_sensor_humidity_read: 'ym_th1_read_humidity',
  ym_th1_humidity_read: 'ym_th1_read_humidity',
  th1_read_humidity: 'ym_th1_read_humidity',
  ym_buzzer_beep: 'ym_beep_set_power',
  ym_buzzer_set_power: 'ym_beep_set_power',
  ym_buzzer_set_frequency: 'ym_beep_set_frequency',
  ym_beeper_beep: 'ym_beep_set_power',
  // Scratch control aliases that some models still emit
  controls_wait: 'scratch_control_wait',
  control_wait: 'scratch_control_wait',
  math_control_wait: 'scratch_control_wait',
  controls_wait_until: 'scratch_control_wait_until',
  control_wait_until: 'scratch_control_wait_until',
  math_control_wait_until: 'scratch_control_wait_until',
  // Legacy/custom text formatting aliases
  math_number_format: 'text_number_to_string_custom',
  text_number_format: 'text_number_to_string_custom',
  number_format: 'text_number_to_string_custom',
  // Legacy JSON field-chain aliases
  json_add_field: 'json_object_set_string',
};

const UI_CREATE_BLOCK_KINDS: Record<string, string> = {
  yc_ui_screen_create: 'screen',
  yc_ui_label_create: 'label',
  yc_ui_button_create: 'button',
  yc_ui_checkbox_create: 'checkbox',
  yc_ui_switch_create: 'switch',
  yc_ui_input_create: 'input',
  yc_ui_slider_create: 'slider',
  yc_ui_progress_create: 'progress',
  yc_ui_image_create: 'image',
  yc_ui_arc_create: 'arc',
  yc_ui_dropdown_create: 'dropdown',
  yc_ui_list_create: 'list',
};

const UI_CREATE_INLINE_SETTERS: Record<string, Array<{
  legacyValueNames: string[];
  setterType: string;
  setterInputName: string;
}>> = {
  yc_ui_button_create: [
    {
      legacyValueNames: ['TEXT', 'LABEL', 'CONTENT'],
      setterType: 'yc_ui_button_set_text',
      setterInputName: 'TEXT',
    },
  ],
  yc_ui_checkbox_create: [
    {
      legacyValueNames: ['TEXT', 'LABEL', 'CONTENT'],
      setterType: 'yc_ui_checkbox_set_text',
      setterInputName: 'TEXT',
    },
  ],
  yc_ui_input_create: [
    {
      legacyValueNames: ['TEXT', 'VALUE', 'CONTENT'],
      setterType: 'yc_ui_input_set_text',
      setterInputName: 'TEXT',
    },
    {
      legacyValueNames: ['PLACEHOLDER', 'PLACEHOLDER_TEXT'],
      setterType: 'yc_ui_input_set_placeholder',
      setterInputName: 'TEXT',
    },
  ],
  yc_ui_image_create: [
    {
      legacyValueNames: ['SRC', 'SOURCE'],
      setterType: 'yc_ui_image_set_src',
      setterInputName: 'SRC',
    },
  ],
  yc_ui_dropdown_create: [
    {
      legacyValueNames: ['OPTIONS', 'ITEMS'],
      setterType: 'yc_ui_dropdown_set_options',
      setterInputName: 'OPTIONS',
    },
  ],
};

const UI_FIELD_HANDLE_BLOCKS: Record<string, string> = {
  yc_ui_label_set_text: 'label',
  yc_ui_label_set_align: 'label',
  yc_ui_label_set_font: 'label',
  yc_ui_label_set_text_color: 'label',
  yc_ui_label_set_bg_color: 'label',
  yc_ui_label_set_pos: 'label',
  yc_ui_label_set_size: 'label',
  yc_ui_label_set_width: 'label',
  yc_ui_label_set_height: 'label',
  yc_ui_obj_center: 'label',
  yc_ui_obj_set_scrollbar_mode: 'label',
  yc_ui_obj_add_flag: 'label',
  yc_ui_obj_clear_flag: 'label',
  yc_ui_obj_has_flag: 'label',
  yc_ui_label_set_long_mode: 'label',
};

const JSON_NODE_VARIABLE_TYPE = 'JsonNode';
const JSON_ARRAY_VARIABLE_TYPE = 'JsonArray';
const MQTT_RUNTIME_PASSWORD_INPUT_BLOCK_TYPES = [
  { type: 'iot_mqtt_init', inputName: 'PASSWORD' },
  { type: 'ym_5g_set_password', inputName: 'PASSWORD' },
  { type: 'ym_5g_set_mqtt_config', inputName: 'PASSWORD' },
] as const;
const LEGACY_JSON_OBJECT_CHILD_BLOCK_TYPES = new Set([
  'json_parse',
  'json_parse_text',
  'json_create_object',
  'json_create_array',
  'json_object_get',
  'json_array_get',
]);

let lastWorkspaceImportError = '';
let lastWorkspaceImportWarning = '';

function setWorkspaceImportError(message: string): void {
  lastWorkspaceImportError = message;
}

function setWorkspaceImportWarning(message: string): void {
  lastWorkspaceImportWarning = message;
}

function clearWorkspaceImportError(): void {
  lastWorkspaceImportError = '';
  lastWorkspaceImportWarning = '';
}

function stripMarkdownCodeFence(text: string): string {
  const trimmed = normalizePossiblyEscapedXml(text).trim();
  const fenced = trimmed.match(/^```(?:xml)?\s*([\s\S]*?)\s*```$/i);
  if (fenced?.[1]) {
    return normalizePossiblyEscapedXml(fenced[1]).trim();
  }
  return trimmed;
}

function ensureXmlRoot(xmlText: string): string {
  if (/^<\s*xml\b/i.test(xmlText)) {
    return xmlText;
  }
  return `<xml>${xmlText}</xml>`;
}

function getElementTagName(element: Element | null | undefined): string {
  if (!element) {
    return '';
  }

  return (element.localName || element.tagName || '').toLowerCase();
}

function isElementNode(node: Node | null): node is Element {
  return node?.nodeType === 1;
}

function collectDescendantElementsByTag(root: Element, tagNames: string[]): Element[] {
  const targetTagNames = new Set(tagNames.map((tagName) => tagName.toLowerCase()));
  const elements: Element[] = [];

  const visit = (node: Element): void => {
    Array.from(node.children).forEach((child) => {
      if (!isElementNode(child)) {
        return;
      }

      if (targetTagNames.has(getElementTagName(child))) {
        elements.push(child);
      }

      visit(child);
    });
  };

  visit(root);
  return elements;
}

function collectTypedNodes(root: Element): Element[] {
  const nodes: Element[] = [];

  const visit = (node: Element): void => {
    const tagName = getElementTagName(node);
    const nodeType = node.getAttribute('type')?.trim();
    if ((tagName === 'block' || tagName === 'shadow') && nodeType) {
      nodes.push(node);
    }

    Array.from(node.children).forEach((child) => {
      if (isElementNode(child)) {
        visit(child);
      }
    });
  };

  visit(root);
  return nodes;
}

function getDirectChildElementsByTag(parent: Element, tagName: string): Element[] {
  const loweredTagName = tagName.toLowerCase();
  return Array.from(parent.children)
    .filter(isElementNode)
    .filter((child) => getElementTagName(child) === loweredTagName);
}

function getDirectNamedChildByTag(parent: Element, tagName: string, name: string): Element | null {
  const candidates = getDirectChildElementsByTag(parent, tagName);
  return candidates.find((child) => (child.getAttribute('name') || '').trim() === name) || null;
}

function getDirectNamedChildFromAliases(parent: Element, tagName: string, names: string[]): Element | null {
  for (const name of names) {
    const child = getDirectNamedChildByTag(parent, tagName, name);
    if (child) {
      return child;
    }
  }
  return null;
}

function getFirstDirectChildByTag(parent: Element, tagName: string): Element | null {
  return getDirectChildElementsByTag(parent, tagName)[0] || null;
}

function migrateLegacyPreservedCScriptSource(xmlDom: Element): void {
  const legacyBlock = collectTypedNodes(xmlDom)
    .find((block) => (block.getAttribute('type') || '').trim() === 'cscript_preserved_source');
  if (!legacyBlock) return;

  const source = (getDirectNamedChildByTag(legacyBlock, 'field', 'CODE')?.textContent || '').trim();
  const targetBlock = collectTypedNodes(xmlDom).find((block) => block !== legacyBlock);
  if (!source || !targetBlock || !targetBlock.ownerDocument) return;

  const existingData = getFirstDirectChildByTag(targetBlock, 'data');
  if (!existingData) {
    const data = targetBlock.ownerDocument.createElement('data');
    data.textContent = `cscript-preserved:${source}`;
    targetBlock.insertBefore(data, targetBlock.firstChild);
  }
  legacyBlock.parentElement?.removeChild(legacyBlock);
}

function createRuntimePasswordPlaceholderValue(ownerDocument: Document, inputName: string): Element {
  const valueElement = ownerDocument.createElement('value');
  valueElement.setAttribute('name', inputName);

  const shadowElement = ownerDocument.createElement('shadow');
  shadowElement.setAttribute('type', 'text');

  const fieldElement = ownerDocument.createElement('field');
  fieldElement.setAttribute('name', 'TEXT');
  fieldElement.textContent = MQTT_RUNTIME_PASSWORD_PLACEHOLDER;

  shadowElement.appendChild(fieldElement);
  valueElement.appendChild(shadowElement);
  return valueElement;
}

function shouldSanitizeRuntimePasswordValue(valueElement: Element): boolean {
  const fieldTexts = collectDescendantElementsByTag(valueElement, ['field'])
    .map((fieldElement) => String(fieldElement.textContent || '').trim());

  if (fieldTexts.length === 0) {
    return valueElement.children.length > 0;
  }

  if (fieldTexts.every((text) => !text)) {
    return false;
  }

  return !fieldTexts.every((text) => isRuntimeMqttPasswordPlaceholder(text));
}

function sanitizeRuntimeMqttPasswordInputs(xmlDom: Element): void {
  collectTypedNodes(xmlDom).forEach((blockElement) => {
    const blockType = (blockElement.getAttribute('type') || '').trim();
    if (!blockType) {
      return;
    }

    MQTT_RUNTIME_PASSWORD_INPUT_BLOCK_TYPES.forEach(({ type, inputName }) => {
      if (blockType !== type) {
        return;
      }

      const passwordValue = getDirectNamedChildByTag(blockElement, 'value', inputName);
      if (!passwordValue || !shouldSanitizeRuntimePasswordValue(passwordValue)) {
        return;
      }

      const ownerDocument = blockElement.ownerDocument;
      if (!ownerDocument) {
        return;
      }

      const placeholderValue = createRuntimePasswordPlaceholderValue(ownerDocument, inputName);
      blockElement.replaceChild(placeholderValue, passwordValue);
    });
  });
}

export function sanitizeSensitiveWorkspaceXml(xmlText: string): string {
  try {
    const rawText = stripMarkdownCodeFence(xmlText);
    if (!rawText) {
      return xmlText;
    }

    const wrappedText = ensureXmlRoot(rawText);
    const xmlDom = Blockly.utils.xml.textToDom(wrappedText) as Element;
    sanitizeRuntimeMqttPasswordInputs(xmlDom);
    return Blockly.Xml.domToText(xmlDom);
  } catch {
    return xmlText;
  }
}

interface VariableReference {
  variableName: string;
  variableId: string;
  variableType: string;
}

interface XmlVariableDeclarationRecord extends VariableReference {
  element: Element;
  key: string;
}

interface XmlVariableFieldRecord extends VariableReference {
  element: Element;
  key: string;
}

const VARIABLE_FIELD_NAMES = new Set(['VAR', 'OBJ_VAR', 'PARENT_VAR', 'HANDLE_VAR']);

const UI_VARIABLE_NAME_HINTS: Record<string, string[]> = {
  screen: ['screen', 'scr'],
  label: ['label', 'lbl'],
  button: ['button', 'btn'],
  input: ['input', 'textarea', 'ta'],
  slider: ['slider'],
  progress: ['progress', 'bar'],
  image: ['image', 'img'],
  checkbox: ['checkbox', 'check'],
  switch: ['switch'],
  arc: ['arc'],
  dropdown: ['dropdown', 'dd'],
  list: ['list'],
};

const PERIPHERAL_OBJECT_NAME_HINTS: Record<string, string> = {
  au: 'YM-AU',
  beep: 'YM-BEEP',
  brg: 'YM-BRG',
  dlgt: 'YM-DLGT',
  imu9: 'YM-IMU9',
  k2beep: 'YM-K2BEEP',
  k3: 'YM-K3',
  led: 'YM-LED',
  nlgt: 'YM-NLGT',
  pir: 'YM-PIR',
  ryg: 'YM-RYG',
  sound: 'YM-SOUND',
  swusb: 'YM-SWUSB',
  th1: 'YM-TH1',
  gnss: 'YM-GNSS',
  us: 'YM-US',
  '5g': 'YM-5G',
};

function toPeripheralBaseVariableName(peripheralId: string): string {
  return peripheralId
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'obj';
}

function getUiVariableType(kind: string): string {
  return `UIObject:${kind}`;
}

function getUiDefaultVariableName(kind: string): string {
  return `ui_${kind}_1`;
}

function inferUiVariableTypeFromName(variableName: string): string {
  const normalizedName = variableName.trim().toLowerCase();
  const matchedEntry = Object.entries(UI_VARIABLE_NAME_HINTS)
    .find(([, hints]) => hints.some((hint) => (
      normalizedName === hint
      || normalizedName.startsWith(`${hint}_`)
      || normalizedName.endsWith(`_${hint}`)
      || normalizedName.includes(`_${hint}_`)
      || normalizedName.startsWith(`ui_${hint}`)
      || (hint.length > 2 && normalizedName.includes(hint))
    )));
  return matchedEntry ? getUiVariableType(matchedEntry[0]) : '';
}

function inferPeripheralObjectTypeFromName(variableName: string): string {
  const normalizedName = variableName.trim().toLowerCase();
  const matched = normalizedName.match(/^(?:ym|yn)_([a-z0-9]+)(?:_|$)/);
  if (matched) {
    const peripheralId = PERIPHERAL_OBJECT_NAME_HINTS[matched[1]];
    return peripheralId ? `PeripheralObject:${peripheralId}` : '';
  }

  const matchedEntry = Object.entries(PERIPHERAL_OBJECT_NAME_HINTS)
    .find(([key]) => (
      normalizedName === key
      || normalizedName.startsWith(`${key}_`)
      || normalizedName.endsWith(`_${key}`)
      || normalizedName.includes(`_${key}_`)
    ));

  return matchedEntry ? `PeripheralObject:${matchedEntry[1]}` : '';
}

function inferPeripheralIdFromDeviceId(deviceId: string): string {
  const normalizedDeviceId = deviceId.trim().toUpperCase();
  const matched = normalizedDeviceId.match(/^((?:YM|YN)-[A-Z0-9]+)(?:-|$)/);
  return matched?.[1] || '';
}

function inferPeripheralIdFromYmBlockType(blockType: string): string {
  const normalizedBlockType = blockType.trim().toLowerCase();
  const matched = normalizedBlockType.match(/^ym_([a-z0-9]+)(?:_|$)/);
  if (!matched) return '';

  return PERIPHERAL_OBJECT_NAME_HINTS[matched[1]] || '';
}

function inferVariableTypeFromName(variableName: string): string {
  return inferUiVariableTypeFromName(variableName)
    || inferPeripheralObjectTypeFromName(variableName);
}

function getVariableTypeAttribute(element: Element | null): string {
  if (!element) return '';
  return (element.getAttribute('variabletype') || element.getAttribute('variableType') || '').trim();
}

function setVariableTypeAttribute(element: Element, variableType: string): void {
  if (!variableType) return;
  element.setAttribute('variabletype', variableType);
}

function getVariableDeclarationType(element: Element | null): string {
  if (!element) return '';
  return (element.getAttribute('type') || '').trim();
}

function setVariableDeclarationType(element: Element, variableType: string): void {
  if (variableType) {
    element.setAttribute('type', variableType);
  } else {
    element.removeAttribute('type');
  }
}

function getVariableNameKey(variableName: string): string {
  return variableName.trim();
}

function getVariableKey(variableName: string, variableType = ''): string {
  return `${variableType}\u0000${getVariableNameKey(variableName)}`;
}

function addStringSetValue(map: Map<string, Set<string>>, key: string, value: string): void {
  if (!key || !value) return;

  let values = map.get(key);
  if (!values) {
    values = new Set<string>();
    map.set(key, values);
  }

  values.add(value);
}

function getFirstStringSetValue(map: Map<string, Set<string>>, key: string): string {
  const values = map.get(key);
  if (!values || values.size === 0) {
    return '';
  }

  return Array.from(values)[0] || '';
}

function isVariableFieldElement(fieldElement: Element): boolean {
  const fieldName = (fieldElement.getAttribute('name') || '').trim();
  return VARIABLE_FIELD_NAMES.has(fieldName)
    || Boolean(getVariableTypeAttribute(fieldElement))
    || Boolean((fieldElement.getAttribute('id') || '').trim());
}

function getXmlVariableElements(xmlDom: Element): Element[] {
  const variablesElement = getFirstDirectChildByTag(xmlDom, 'variables');
  return variablesElement ? getDirectChildElementsByTag(variablesElement, 'variable') : [];
}

function ensureXmlVariablesElement(xmlDom: Element): Element {
  const existingVariablesElement = getFirstDirectChildByTag(xmlDom, 'variables');
  if (existingVariablesElement) {
    return existingVariablesElement;
  }

  const variablesElement = xmlDom.ownerDocument.createElement('variables');
  const firstElementChild = Array.from(xmlDom.children)
    .find(isElementNode) || null;

  if (firstElementChild) {
    xmlDom.insertBefore(variablesElement, firstElementChild);
  } else {
    xmlDom.appendChild(variablesElement);
  }

  return variablesElement;
}

function collectFieldVariableRecords(
  xmlDom: Element,
  declarationTypeById: Map<string, string>,
  declarationTypesByName: Map<string, Set<string>>
): XmlVariableFieldRecord[] {
  return collectDescendantElementsByTag(xmlDom, ['field'])
    .filter((element): element is Element => isElementNode(element) && isVariableFieldElement(element))
    .map((fieldElement) => {
      const variableName = (fieldElement.textContent || '').trim();
      if (!variableName) {
        return null;
      }

      const variableId = (fieldElement.getAttribute('id') || '').trim();
      const variableType = getVariableTypeAttribute(fieldElement)
        || (variableId ? declarationTypeById.get(variableId) || '' : '')
        || getFirstStringSetValue(declarationTypesByName, getVariableNameKey(variableName))
        || inferVariableTypeFromName(variableName);

      return {
        element: fieldElement,
        variableName,
        variableId,
        variableType,
        key: getVariableKey(variableName, variableType),
      };
    })
    .filter((record): record is XmlVariableFieldRecord => Boolean(record));
}

function normalizeVariableReferenceIds(xmlDom: Element, workspace?: Blockly.Workspace): string[] {
  const warnings: string[] = [];
  const declarationElements = getXmlVariableElements(xmlDom);

  const explicitDeclarationTypeById = new Map<string, string>();
  const explicitDeclarationTypesByName = new Map<string, Set<string>>();
  declarationElements.forEach((variableElement) => {
    const variableName = (variableElement.textContent || '').trim();
    if (!variableName) return;

    const variableId = (variableElement.getAttribute('id') || '').trim();
    const variableType = getVariableDeclarationType(variableElement);
    if (variableId && variableType) {
      explicitDeclarationTypeById.set(variableId, variableType);
    }
    if (variableType) {
      addStringSetValue(explicitDeclarationTypesByName, getVariableNameKey(variableName), variableType);
    }
  });

  let fieldRecords = collectFieldVariableRecords(xmlDom, explicitDeclarationTypeById, explicitDeclarationTypesByName);

  const fieldTypeById = new Map<string, string>();
  const fieldTypesByName = new Map<string, Set<string>>();
  fieldRecords.forEach((record) => {
    if (record.variableId && record.variableType) {
      fieldTypeById.set(record.variableId, record.variableType);
    }
    if (record.variableType) {
      addStringSetValue(fieldTypesByName, getVariableNameKey(record.variableName), record.variableType);
    }
  });

  const declarationRecords = declarationElements
    .map((variableElement) => {
      const variableName = (variableElement.textContent || '').trim();
      if (!variableName) {
        return null;
      }

      const variableId = (variableElement.getAttribute('id') || '').trim();
      const variableType = getVariableDeclarationType(variableElement)
        || (variableId ? fieldTypeById.get(variableId) || '' : '')
        || getFirstStringSetValue(fieldTypesByName, getVariableNameKey(variableName))
        || inferVariableTypeFromName(variableName);

      return {
        element: variableElement,
        variableName,
        variableId,
        variableType,
        key: getVariableKey(variableName, variableType),
      };
    })
    .filter((record): record is XmlVariableDeclarationRecord => Boolean(record));

  const declarationTypeById = new Map<string, string>();
  const declarationTypesByName = new Map<string, Set<string>>();
  declarationRecords.forEach((record) => {
    if (record.variableId && record.variableType) {
      declarationTypeById.set(record.variableId, record.variableType);
    }
    if (record.variableType) {
      addStringSetValue(declarationTypesByName, getVariableNameKey(record.variableName), record.variableType);
    }
  });

  fieldRecords = fieldRecords.map((record) => {
    if (record.variableType) {
      return record;
    }

    const variableType = (record.variableId ? declarationTypeById.get(record.variableId) || '' : '')
      || getFirstStringSetValue(declarationTypesByName, getVariableNameKey(record.variableName))
      || inferVariableTypeFromName(record.variableName);

    return {
      ...record,
      variableType,
      key: getVariableKey(record.variableName, variableType),
    };
  });

  const canonicalByKey = new Map<string, VariableReference>();
  const usedVariableIds = new Set<string>();
  workspace?.getAllVariables().forEach((variable) => {
    const variableName = variable.name;
    const variableType = variable.type || '';
    usedVariableIds.add(variable.getId());
    canonicalByKey.set(getVariableKey(variableName, variableType), {
      variableName,
      variableType,
      variableId: variable.getId(),
    });
  });

  const allocateUniqueVariableId = (preferredId: string): string => {
    const normalizedPreferredId = preferredId.trim();
    if (normalizedPreferredId && !usedVariableIds.has(normalizedPreferredId)) {
      usedVariableIds.add(normalizedPreferredId);
      return normalizedPreferredId;
    }

    let generatedId = Blockly.utils.idGenerator.genUid();
    while (usedVariableIds.has(generatedId)) {
      generatedId = Blockly.utils.idGenerator.genUid();
    }
    usedVariableIds.add(generatedId);
    return generatedId;
  };

  let duplicateVariableIdAdjusted = false;

  const ensureCanonicalVariable = (reference: VariableReference): VariableReference => {
    const key = getVariableKey(reference.variableName, reference.variableType);
    const existing = canonicalByKey.get(key);
    if (existing) {
      return existing;
    }

    const canonical: VariableReference = {
      variableName: reference.variableName,
      variableType: reference.variableType,
      variableId: allocateUniqueVariableId(reference.variableId),
    };
    if (reference.variableId.trim() && canonical.variableId !== reference.variableId.trim()) {
      duplicateVariableIdAdjusted = true;
    }
    canonicalByKey.set(key, canonical);
    return canonical;
  };

  const keptDeclarationKeys = new Set<string>();
  declarationRecords.forEach((record) => {
    const canonical = ensureCanonicalVariable(record);

    if (keptDeclarationKeys.has(record.key)) {
      record.element.parentElement?.removeChild(record.element);
      return;
    }

    record.element.textContent = canonical.variableName;
    record.element.setAttribute('id', canonical.variableId);
    setVariableDeclarationType(record.element, canonical.variableType);
    keptDeclarationKeys.add(record.key);
  });

  fieldRecords.forEach((record) => {
    const canonical = ensureCanonicalVariable(record);
    record.element.textContent = canonical.variableName;
    record.element.setAttribute('id', canonical.variableId);
    if (canonical.variableType) {
      setVariableTypeAttribute(record.element, canonical.variableType);
    }
  });

  const referencedKeys = new Set<string>();
  declarationRecords.forEach((record) => referencedKeys.add(record.key));
  fieldRecords.forEach((record) => referencedKeys.add(record.key));

  if (referencedKeys.size === 0) {
    return warnings;
  }

  const variablesElement = ensureXmlVariablesElement(xmlDom);
  referencedKeys.forEach((key) => {
    if (keptDeclarationKeys.has(key)) {
      return;
    }

    const canonical = canonicalByKey.get(key);
    if (!canonical) {
      return;
    }

    const variableElement = xmlDom.ownerDocument.createElement('variable');
    variableElement.textContent = canonical.variableName;
    variableElement.setAttribute('id', canonical.variableId);
    setVariableDeclarationType(variableElement, canonical.variableType);
    variablesElement.appendChild(variableElement);
    keptDeclarationKeys.add(key);
  });

  if (duplicateVariableIdAdjusted) {
    warnings.push('检测到重复的变量 ID，已自动改写变量引用并继续导入。');
  }

  return warnings;
}

function getVariableReferenceFromField(
  fieldElement: Element | null,
  fallbackName = '',
  fallbackType = ''
): VariableReference | null {
  const variableName = (fieldElement?.textContent || fallbackName).trim();
  if (!variableName) return null;

  const variableId = (fieldElement?.getAttribute('id') || '').trim();
  const variableType = getVariableTypeAttribute(fieldElement)
    || fallbackType
    || inferVariableTypeFromName(variableName);

  return {
    variableName,
    variableId,
    variableType,
  };
}

function extractVariableReferenceFromValue(
  valueElement: Element | null,
  fallbackType = ''
): VariableReference | null {
  if (!valueElement) return null;

  const variableField = valueElement.querySelector('field[name="VAR"], field[name="OBJ_VAR"], field[name="PARENT_VAR"]');
  if (!isElementNode(variableField)) {
    return null;
  }

  return getVariableReferenceFromField(variableField, '', fallbackType);
}

function inferVariableFieldTypeFromContext(blockElement: Element, fieldName: string): string {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  const uiCreateKind = UI_CREATE_BLOCK_KINDS[blockType];
  if (uiCreateKind && fieldName === 'OBJ_VAR') {
    return getUiVariableType(uiCreateKind);
  }

  if (uiCreateKind && fieldName === 'PARENT_VAR' && blockType !== 'yc_ui_screen_create') {
    return getUiVariableType('screen');
  }

  const uiFieldKind = UI_FIELD_HANDLE_BLOCKS[blockType];
  if (uiFieldKind && fieldName === 'OBJ_VAR') {
    return getUiVariableType(uiFieldKind);
  }

  if ((blockType === 'wireless_peripheral_create_object' || blockType === 'wireless_peripheral_set_object') && fieldName === 'OBJ_VAR') {
    const peripheral = (getDirectNamedChildByTag(blockElement, 'field', 'PERIPHERAL')?.textContent || '').trim();
    return peripheral ? `PeripheralObject:${peripheral}` : '';
  }

  return '';
}

function inferVariableFieldTypeFromValueContext(valueElement: Element): string {
  const valueName = (valueElement.getAttribute('name') || '').trim();
  const parentBlock = valueElement.parentElement;
  if (!parentBlock) return '';

  const parentBlockType = (parentBlock.getAttribute('type') || '').trim().toLowerCase();
  if (!parentBlockType) return '';

  if (valueName === 'HANDLE') {
    if (parentBlockType === 'yc_ui_set_active_screen') {
      return getUiVariableType('screen');
    }

    if (parentBlockType.startsWith('ym_')) {
      const peripheralId = inferPeripheralIdFromYmBlockType(parentBlockType);
      return peripheralId ? `PeripheralHandle:${peripheralId}` : '';
    }

    if (parentBlockType === 'wireless_peripheral_set_object') {
      const peripheralId = (
        getDirectNamedChildByTag(parentBlock, 'field', 'PERIPHERAL')?.textContent
        || ''
      ).trim();
      return peripheralId ? `PeripheralHandle:${peripheralId}` : '';
    }
  }

  return '';
}

function normalizeVariableFieldTypeHints(blockElement: Element): void {
  getDirectChildElementsByTag(blockElement, 'field').forEach((fieldElement) => {
    const fieldName = (fieldElement.getAttribute('name') || '').trim();
    if (!['VAR', 'OBJ_VAR', 'PARENT_VAR', 'HANDLE_VAR'].includes(fieldName)) {
      return;
    }

    if (getVariableTypeAttribute(fieldElement)) {
      return;
    }

    const variableName = (fieldElement.textContent || '').trim();
    const inferredType = inferVariableTypeFromName(variableName)
      || inferVariableFieldTypeFromContext(blockElement, fieldName);
    if (inferredType) {
      setVariableTypeAttribute(fieldElement, inferredType);
    }
  });

  getDirectChildElementsByTag(blockElement, 'value').forEach((valueElement) => {
    const inferredType = inferVariableFieldTypeFromValueContext(valueElement);
    if (!inferredType) {
      return;
    }

    collectDescendantElementsByTag(valueElement, ['field']).forEach((fieldElement) => {
      const fieldName = (fieldElement.getAttribute('name') || '').trim();
      if (!['VAR', 'OBJ_VAR', 'PARENT_VAR', 'HANDLE_VAR'].includes(fieldName)) {
        return;
      }

      if (getVariableTypeAttribute(fieldElement)) {
        return;
      }

      setVariableTypeAttribute(fieldElement, inferredType);
    });
  });
}

function convertDirectNamedChildTag(parent: Element, fromTagName: string, toTagName: string, name: string): Element | null {
  const source = getDirectNamedChildByTag(parent, fromTagName, name);
  if (!source) return null;

  const replacement = document.createElement(toTagName);
  Array.from(source.attributes).forEach((attribute) => {
    replacement.setAttribute(attribute.name, attribute.value);
  });

  while (source.firstChild) {
    replacement.appendChild(source.firstChild);
  }

  parent.replaceChild(replacement, source);
  return replacement;
}

function getTailBlock(startBlock: Element): Element {
  let current = startBlock;
  const visited = new Set<Element>();

  while (!visited.has(current)) {
    visited.add(current);
    const nextBlock = getDirectNextBlock(current);
    if (!nextBlock) {
      return current;
    }
    current = nextBlock;
  }

  return current;
}

function normalizeSequentialStatementChildren(root: Element): void {
  collectDescendantElementsByTag(root, ['statement', 'next']).forEach((containerNode) => {
    const directBlocks = getDirectChildElementsByTag(containerNode, 'block');
    if (directBlocks.length <= 1) {
      return;
    }

    let tailBlock = getTailBlock(directBlocks[0]);
    directBlocks.slice(1).forEach((blockElement) => {
      if (blockElement.parentElement === containerNode) {
        containerNode.removeChild(blockElement);
      }
      setDirectNextBlock(tailBlock, blockElement);
      tailBlock = getTailBlock(blockElement);
    });
  });
}

function createLiteralShadowBlock(ownerDocument: Document, shadowType: string, fieldName: string, textContent: string): Element {
  const shadowElement = ownerDocument.createElement('shadow');
  shadowElement.setAttribute('type', shadowType);

  const fieldElement = ownerDocument.createElement('field');
  fieldElement.setAttribute('name', fieldName);
  fieldElement.textContent = textContent;

  shadowElement.appendChild(fieldElement);
  return shadowElement;
}

function normalizeValueLiteralShorthand(root: Element): void {
  collectDescendantElementsByTag(root, ['value']).forEach((valueElement) => {
    const ownerDocument = valueElement.ownerDocument;
    if (!ownerDocument) {
      return;
    }

    const directBlocks = getDirectChildElementsByTag(valueElement, 'block');
    const directShadows = getDirectChildElementsByTag(valueElement, 'shadow');
    if (directBlocks.length > 0 || directShadows.length > 0) {
      return;
    }

    const numberLiteral = getFirstDirectChildByTag(valueElement, 'number');
    if (numberLiteral) {
      const rawValue = (numberLiteral.textContent || '').trim() || '0';
      valueElement.removeChild(numberLiteral);
      valueElement.appendChild(createLiteralShadowBlock(ownerDocument, 'math_number', 'NUM', rawValue));
      return;
    }

    const textLiteral = getFirstDirectChildByTag(valueElement, 'text');
    if (textLiteral) {
      const rawValue = (textLiteral.textContent || '').trim();
      valueElement.removeChild(textLiteral);
      valueElement.appendChild(createLiteralShadowBlock(ownerDocument, 'text', 'TEXT', rawValue));
      return;
    }

    const booleanLiteral = getFirstDirectChildByTag(valueElement, 'boolean');
    if (booleanLiteral) {
      const rawValue = (booleanLiteral.textContent || '').trim().toUpperCase() === 'TRUE' ? 'TRUE' : 'FALSE';
      valueElement.removeChild(booleanLiteral);
      valueElement.appendChild(createLiteralShadowBlock(ownerDocument, 'logic_boolean', 'BOOL', rawValue));
    }
  });
}

function normalizeDetachedRootNextElements(root: Element): void {
  if (getElementTagName(root) !== 'xml') {
    return;
  }

  let currentTail: Element | null = null;

  Array.from(root.children).forEach((child) => {
    if (!isElementNode(child)) {
      return;
    }

    const tagName = getElementTagName(child);
    if (tagName === 'block') {
      const profile = getBlockConnectionProfile((child.getAttribute('type') || '').trim());
      currentTail = profile && !profile.hasOutput ? getTailBlock(child) : null;
      return;
    }

    if (tagName !== 'next' || !currentTail) {
      return;
    }

    const chainedBlock = getFirstDirectChildByTag(child, 'block');
    root.removeChild(child);
    if (!chainedBlock) {
      return;
    }

    setDirectNextBlock(currentTail, chainedBlock);
    currentTail = getTailBlock(chainedBlock);
  });
}

function normalizeSequentialRootBlocks(root: Element): void {
  if (getElementTagName(root) !== 'xml') {
    return;
  }

  const directBlocks = getDirectChildElementsByTag(root, 'block');
  if (directBlocks.length <= 1) {
    return;
  }

  let currentTail: Element | null = null;
  let currentTailProfile: BlockConnectionProfile | null = null;
  let currentAnchor: Element | null = null;

  directBlocks.forEach((blockElement) => {
    const blockType = (blockElement.getAttribute('type') || '').trim();
    const profile = getBlockConnectionProfile(blockType);

    if (!profile || profile.hasOutput) {
      currentTail = null;
      currentTailProfile = null;
      currentAnchor = null;
      return;
    }

    if (
      currentTail
      && currentTailProfile?.hasNext
      && profile.hasPrevious
      && shouldChainSequentialRootElements(currentAnchor, blockElement)
    ) {
      if (blockElement.parentElement === root) {
        root.removeChild(blockElement);
      }

      setDirectNextBlock(currentTail, blockElement);
      currentTail = getTailBlock(blockElement);
      currentTailProfile = getBlockConnectionProfile((currentTail.getAttribute('type') || '').trim());
      currentAnchor = blockElement;

      if (!currentTailProfile?.hasNext) {
        currentTail = null;
        currentTailProfile = null;
        currentAnchor = null;
      }
      return;
    }

    currentTail = getTailBlock(blockElement);
    currentTailProfile = getBlockConnectionProfile((currentTail.getAttribute('type') || '').trim());
    currentAnchor = blockElement;

    if (!currentTailProfile?.hasNext) {
      currentTail = null;
      currentTailProfile = null;
      currentAnchor = null;
    }
  });
}

function ensureControlsIfMutation(blockElement: Element): void {
  const existingMutation = getFirstDirectChildByTag(blockElement, 'mutation');
  if (existingMutation) {
    return;
  }

  const mutationElement = document.createElement('mutation');
  if (blockElement.firstChild) {
    blockElement.insertBefore(mutationElement, blockElement.firstChild);
  } else {
    blockElement.appendChild(mutationElement);
  }
}

function normalizeControlsIfMutation(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (blockType !== 'controls_if') return;

  convertDirectNamedChildTag(blockElement, 'value', 'statement', 'ELSE');

  getDirectChildElementsByTag(blockElement, 'value').forEach((valueElement) => {
    const name = (valueElement.getAttribute('name') || '').trim();
    const elseifMatch = name.match(/^ELSEIF(\d+)$/);
    if (elseifMatch) {
      valueElement.setAttribute('name', `IF${Number(elseifMatch[1]) + 1}`);
    }
  });

  getDirectChildElementsByTag(blockElement, 'value').forEach((valueElement) => {
    const name = (valueElement.getAttribute('name') || '').trim();
    if (/^DO\d+$/.test(name)) {
      convertDirectNamedChildTag(blockElement, 'value', 'statement', name);
    }
  });

  const indexes = new Set<number>();
  getDirectChildElementsByTag(blockElement, 'value').forEach((valueElement) => {
    const matched = (valueElement.getAttribute('name') || '').trim().match(/^IF(\d+)$/);
    if (matched) {
      indexes.add(Number(matched[1]));
    }
  });
  getDirectChildElementsByTag(blockElement, 'statement').forEach((statementElement) => {
    const matched = (statementElement.getAttribute('name') || '').trim().match(/^DO(\d+)$/);
    if (matched) {
      indexes.add(Number(matched[1]));
    }
  });

  const hasElse = Boolean(getDirectNamedChildByTag(blockElement, 'statement', 'ELSE'));
  const elseifCount = indexes.size > 0 ? Math.max(...indexes) : 0;
  if (!hasElse && elseifCount === 0 && !getFirstDirectChildByTag(blockElement, 'mutation')) {
    return;
  }

  ensureControlsIfMutation(blockElement);
  const mutationElement = getFirstDirectChildByTag(blockElement, 'mutation');
  if (!mutationElement) return;

  mutationElement.setAttribute('elseif', String(Math.max(0, elseifCount)));
  if (hasElse) {
    mutationElement.setAttribute('else', '1');
  } else {
    mutationElement.removeAttribute('else');
  }
}

function normalizeColorLiteral(rawColor: string): string {
  const normalized = rawColor.trim();
  if (!normalized) return '';

  if (/^#[0-9a-f]{6}$/i.test(normalized)) {
    return normalized.toUpperCase();
  }

  if (/^#[0-9a-f]{3}$/i.test(normalized)) {
    return `#${normalized.slice(1).split('').map((char) => `${char}${char}`).join('')}`.toUpperCase();
  }

  if (/^0x[0-9a-f]{6}$/i.test(normalized)) {
    return `#${normalized.slice(2)}`.toUpperCase();
  }

  if (/^\d+$/i.test(normalized)) {
    const parsed = Number(normalized);
    if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 0xFFFFFF) {
      return `#${parsed.toString(16).padStart(6, '0')}`.toUpperCase();
    }
  }

  return '';
}

function extractColorLiteralFromValue(valueElement: Element | null): string {
  if (!valueElement) return '';

  const blockElement = valueElement.querySelector('block, shadow');
  if (!isElementNode(blockElement)) {
    return '';
  }

  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (blockType === 'colour_picker') {
    return normalizeColorLiteral(blockElement.querySelector('field[name="COLOUR"]')?.textContent || '');
  }

  if (blockType === 'text') {
    return normalizeColorLiteral(blockElement.querySelector('field[name="TEXT"]')?.textContent || '');
  }

  if (blockType === 'math_number') {
    return normalizeColorLiteral(blockElement.querySelector('field[name="NUM"]')?.textContent || '');
  }

  return '';
}

function normalizeUiColorFieldInput(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (blockType !== 'yc_ui_label_set_text_color' && blockType !== 'yc_ui_label_set_bg_color') {
    return;
  }

  const legacyColorValue = takeDirectNamedChild(blockElement, 'value', 'COLOR');
  if (!legacyColorValue) {
    return;
  }

  const normalizedColor = extractColorLiteralFromValue(legacyColorValue);
  if (normalizedColor) {
    upsertDirectField(blockElement, 'COLOR', normalizedColor);
  }
}

function takeDirectNamedChild(parent: Element, tagName: string, name: string): Element | null {
  const child = getDirectNamedChildByTag(parent, tagName, name);
  if (!child) return null;
  parent.removeChild(child);
  return child;
}

function takeDirectNamedChildFromAliases(parent: Element, tagName: string, names: string[]): Element | null {
  for (const name of names) {
    const child = takeDirectNamedChild(parent, tagName, name);
    if (child) {
      return child;
    }
  }
  return null;
}

function ensureVariableField(parent: Element, fieldName: string, reference: VariableReference): Element {
  const existingField = getDirectNamedChildByTag(parent, 'field', fieldName);
  const fieldElement = existingField || parent.ownerDocument.createElement('field');

  if (!existingField) {
    fieldElement.setAttribute('name', fieldName);
    const nextEl = getFirstDirectChildByTag(parent, 'next');
    if (nextEl) {
      parent.insertBefore(fieldElement, nextEl);
    } else {
      parent.appendChild(fieldElement);
    }
  }

  fieldElement.textContent = reference.variableName;

  if (reference.variableId) {
    fieldElement.setAttribute('id', reference.variableId);
  }

  setVariableTypeAttribute(fieldElement, reference.variableType);
  return fieldElement;
}

function createVariablesGetShadow(reference: VariableReference): Element {
  const shadowElement = document.createElement('shadow');
  shadowElement.setAttribute('type', 'variables_get');

  const fieldElement = document.createElement('field');
  fieldElement.setAttribute('name', 'VAR');
  fieldElement.textContent = reference.variableName;

  if (reference.variableId) {
    fieldElement.setAttribute('id', reference.variableId);
  }

  setVariableTypeAttribute(fieldElement, reference.variableType);
  shadowElement.appendChild(fieldElement);
  return shadowElement;
}

function getPreferredPeripheralHandleReference(
  xmlDom: Element,
  peripheralId: string,
  workspace?: Blockly.Workspace,
): VariableReference {
  const variableType = `PeripheralHandle:${peripheralId}`;

  const workspaceVariable = workspace?.getVariablesOfType(variableType)
    .sort((left, right) => left.name.localeCompare(right.name))[0];
  if (workspaceVariable) {
    return {
      variableName: workspaceVariable.name,
      variableId: workspaceVariable.getId(),
      variableType,
    };
  }

  const declaredVariable = getXmlVariableElements(xmlDom)
    .filter((element) => getVariableDeclarationType(element) === variableType)
    .map((element) => ({
      variableName: (element.textContent || '').trim(),
      variableId: (element.getAttribute('id') || '').trim(),
      variableType,
    }))
    .filter((reference) => Boolean(reference.variableName))
    .sort((left, right) => left.variableName.localeCompare(right.variableName))[0];
  if (declaredVariable) {
    return declaredVariable;
  }

  const referencedField = collectDescendantElementsByTag(xmlDom, ['field'])
    .find((fieldElement) => getVariableTypeAttribute(fieldElement) === variableType);
  if (referencedField) {
    const fieldReference = getVariableReferenceFromField(referencedField, '', variableType);
    if (fieldReference) {
      return fieldReference;
    }
  }

  return {
    variableName: `${toPeripheralBaseVariableName(peripheralId)}_1`,
    variableId: '',
    variableType,
  };
}

function ensureYmHandleInputs(xmlDom: Element, workspace?: Blockly.Workspace): void {
  collectTypedNodes(xmlDom).forEach((blockElement) => {
    const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
    if (!blockType.startsWith('ym_')) return;
    if (blockType.includes('_when_')) return;
    if (blockType === 'ym_gnss_calculate_distance') return;
    if (getDirectNamedChildByTag(blockElement, 'value', 'HANDLE')) return;

    const peripheralId = inferPeripheralIdFromYmBlockType(blockType);
    if (!peripheralId) return;

    const handleValue = document.createElement('value');
    handleValue.setAttribute('name', 'HANDLE');
    handleValue.appendChild(createVariablesGetShadow(
      getPreferredPeripheralHandleReference(xmlDom, peripheralId, workspace)
    ));

    const nextEl = getFirstDirectChildByTag(blockElement, 'next');
    if (nextEl) {
      blockElement.insertBefore(handleValue, nextEl);
    } else {
      blockElement.appendChild(handleValue);
    }
  });
}

function appendBlocksAfter(parentBlock: Element, appendedBlocks: Element[]): void {
  if (appendedBlocks.length === 0) return;

  const originalNextBlock = getDirectNextBlock(parentBlock);
  setDirectNextBlock(parentBlock, appendedBlocks[0]);

  appendedBlocks.forEach((block, index) => {
    const nextBlock = appendedBlocks[index + 1] || originalNextBlock;
    setDirectNextBlock(block, nextBlock || null);
  });
}

type LegacyJsonObjectSetterType =
  | 'json_object_set_string'
  | 'json_object_set_number'
  | 'json_object_set_bool'
  | 'json_object_set_null'
  | 'json_object_set_json';

interface LegacyJsonAssignmentContext {
  assignmentBlock: Element;
  objectReference: VariableReference;
}

function getDirectTypedChild(parent: Element | null): Element | null {
  if (!parent) {
    return null;
  }

  return getFirstDirectChildByTag(parent, 'block') || getFirstDirectChildByTag(parent, 'shadow');
}

function getLegacyJsonObjectItemIndexes(blockElement: Element): number[] {
  const indexes = new Set<number>();

  getDirectChildElementsByTag(blockElement, 'value').forEach((valueElement) => {
    const matched = ((valueElement.getAttribute('name') || '').trim()).match(/^(?:KEY|VALUE)(\d+)$/);
    if (matched) {
      indexes.add(Number(matched[1]));
    }
  });

  const mutationItems = Number(getFirstDirectChildByTag(blockElement, 'mutation')?.getAttribute('items') || '');
  if (Number.isFinite(mutationItems) && mutationItems > 0) {
    for (let index = 0; index < mutationItems; index += 1) {
      indexes.add(index);
    }
  }

  return Array.from(indexes).sort((left, right) => left - right);
}

function isStatementLikeBlockContext(blockElement: Element): boolean {
  const parentTag = getElementTagName(blockElement.parentElement);
  return parentTag === 'xml' || parentTag === 'next' || parentTag === 'statement';
}

function createTextShadowValue(inputName: string, text: string): Element {
  const valueElement = document.createElement('value');
  valueElement.setAttribute('name', inputName);

  const shadowElement = document.createElement('shadow');
  shadowElement.setAttribute('type', 'text');

  const fieldElement = document.createElement('field');
  fieldElement.setAttribute('name', 'TEXT');
  fieldElement.textContent = text;

  shadowElement.appendChild(fieldElement);
  valueElement.appendChild(shadowElement);
  return valueElement;
}

function getDirectValueVariableReferenceFromAliases(
  blockElement: Element | null,
  valueNames: string[],
  fallbackType = ''
): VariableReference | null {
  if (!blockElement) {
    return null;
  }

  for (const valueName of valueNames) {
    const reference = extractVariableReferenceFromValue(
      getDirectNamedChildByTag(blockElement, 'value', valueName),
      fallbackType
    );
    if (reference) {
      return reference;
    }
  }

  return null;
}

function getLegacyJsonStatementFieldBlocks(blockElement: Element): Element[] {
  const fieldsStatement = getDirectNamedChildByTag(blockElement, 'statement', 'FIELDS');
  if (!fieldsStatement) {
    return [];
  }

  const blocks: Element[] = [];
  const visited = new Set<Element>();
  let current = getFirstDirectChildByTag(fieldsStatement, 'block');

  while (current && !visited.has(current)) {
    visited.add(current);
    blocks.push(current);
    current = getDirectNextBlock(current);
  }

  return blocks;
}

function getLegacyJsonAssignmentContext(blockElement: Element): LegacyJsonAssignmentContext | null {
  const parentValue = blockElement.parentElement;
  if (getElementTagName(parentValue) !== 'value') {
    return null;
  }

  if (((parentValue?.getAttribute('name') || '').trim()) !== 'VALUE') {
    return null;
  }

  const assignmentBlock = parentValue?.parentElement;
  if (!assignmentBlock || getElementTagName(assignmentBlock) !== 'block') {
    return null;
  }

  const assignmentType = (assignmentBlock.getAttribute('type') || '').trim().toLowerCase();
  if (assignmentType !== 'variables_set') {
    return null;
  }

  const objectReference = getVariableReferenceFromField(
    getDirectNamedChildByTag(assignmentBlock, 'field', 'VAR'),
    'json_object_1',
    JSON_NODE_VARIABLE_TYPE
  );
  if (!objectReference) {
    return null;
  }

  ensureVariableField(assignmentBlock, 'VAR', objectReference);
  return {
    assignmentBlock,
    objectReference,
  };
}

function inferLegacyJsonObjectReference(
  blockElement: Element,
  assignmentContext: LegacyJsonAssignmentContext | null
): VariableReference {
  if (assignmentContext) {
    return assignmentContext.objectReference;
  }

  const hintedReference = getDirectValueVariableReferenceFromAliases(
    getDirectNextBlock(blockElement),
    ['OBJECT', 'VALUE', 'JSON'],
    JSON_NODE_VARIABLE_TYPE
  );
  if (hintedReference) {
    return {
      ...hintedReference,
      variableType: hintedReference.variableType || JSON_NODE_VARIABLE_TYPE,
    };
  }

  return {
    variableName: 'json_object_1',
    variableId: '',
    variableType: JSON_NODE_VARIABLE_TYPE,
  };
}

function inferLegacyJsonStringifyResultReference(blockElement: Element): VariableReference {
  const hintedReference = getDirectValueVariableReferenceFromAliases(
    getDirectNextBlock(blockElement),
    ['PAYLOAD', 'VALUE', 'TEXT'],
    ''
  );
  if (hintedReference) {
    return hintedReference;
  }

  return {
    variableName: 'json_payload',
    variableId: '',
    variableType: '',
  };
}

function getLegacyJsonObjectValueVariableType(valueElement: Element | null): string {
  const directBlock = getDirectTypedChild(valueElement);
  if (!directBlock) {
    return '';
  }

  const variableField = collectDescendantElementsByTag(directBlock, ['field'])
    .find((fieldElement) => Boolean(getVariableTypeAttribute(fieldElement)));

  return getVariableTypeAttribute(variableField || null);
}

function inferLegacyJsonObjectSetterType(valueElement: Element | null): LegacyJsonObjectSetterType {
  const directBlock = getDirectTypedChild(valueElement);
  if (!directBlock) {
    return 'json_object_set_null';
  }

  const variableType = getLegacyJsonObjectValueVariableType(valueElement);
  if (variableType === JSON_NODE_VARIABLE_TYPE || variableType === JSON_ARRAY_VARIABLE_TYPE) {
    return 'json_object_set_json';
  }

  const blockType = (directBlock.getAttribute('type') || '').trim().toLowerCase();
  if (LEGACY_JSON_OBJECT_CHILD_BLOCK_TYPES.has(blockType)) {
    return 'json_object_set_json';
  }

  const outputChecks = getBlockOutputChecks(blockType) || [];
  if (outputChecks.includes('Boolean')) {
    return 'json_object_set_bool';
  }
  if (outputChecks.includes('Number')) {
    return 'json_object_set_number';
  }
  if (outputChecks.includes('String')) {
    return 'json_object_set_string';
  }

  if (blockType.startsWith('logic_')) {
    return 'json_object_set_bool';
  }
  if (blockType.startsWith('math_')) {
    return 'json_object_set_number';
  }

  return 'json_object_set_string';
}

function createJsonObjectSetterBlock(
  setterType: LegacyJsonObjectSetterType,
  objectReference: VariableReference,
  keyValue: Element,
  valueValue: Element | null
): Element {
  const blockElement = document.createElement('block');
  blockElement.setAttribute('type', setterType);

  ensureVariableField(blockElement, 'OBJ', objectReference);
  keyValue.setAttribute('name', 'KEY');
  blockElement.appendChild(keyValue);

  if (valueValue && setterType === 'json_object_set_json') {
    const childReference = extractVariableReferenceFromValue(valueValue);
    if (childReference) {
      ensureVariableField(blockElement, 'CHILD', childReference);
      return blockElement;
    }
  }

  if (valueValue && setterType !== 'json_object_set_null') {
    valueValue.setAttribute('name', setterType === 'json_object_set_json' ? 'CHILD' : 'VALUE');
    blockElement.appendChild(valueValue);
  }

  return blockElement;
}

function createLegacyJsonSetterBlockFromFieldBlock(
  fieldBlock: Element,
  objectReference: VariableReference
): Element | null {
  const inlineKeyValue = takeDirectNamedChildFromAliases(fieldBlock, 'value', ['KEY']);
  const keyField = takeDirectNamedChildFromAliases(fieldBlock, 'field', ['KEY', 'NAME']);
  const keyText = (keyField?.textContent || '').trim();
  const keyValue = inlineKeyValue || (keyText ? createTextShadowValue('KEY', keyText) : null);
  if (!keyValue) {
    return null;
  }

  const valueValue = takeDirectNamedChildFromAliases(fieldBlock, 'value', ['VALUE', 'OBJECT', 'JSON']);
  const setterType = inferLegacyJsonObjectSetterType(valueValue);
  return createJsonObjectSetterBlock(setterType, objectReference, keyValue, valueValue);
}

function normalizeLegacyMqttTopicField(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (blockType !== 'iot_mqtt_publish' && blockType !== 'iot_mqtt_subscribe') {
    return;
  }

  if (getDirectNamedChildByTag(blockElement, 'value', 'TOPIC')) {
    return;
  }

  const topicField = takeDirectNamedChildFromAliases(blockElement, 'field', ['TOPIC']);
  const topicText = (topicField?.textContent || '').trim();
  if (!topicText) {
    return;
  }

  const topicValue = createTextShadowValue('TOPIC', topicText);
  const nextElement = getFirstDirectChildByTag(blockElement, 'next');
  if (nextElement) {
    blockElement.insertBefore(topicValue, nextElement);
  } else {
    blockElement.appendChild(topicValue);
  }
}

function normalizeLegacyMqttInitInputs(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (blockType !== 'iot_mqtt_init') {
    return;
  }

  renameDirectNamedChild(blockElement, 'value', 'USER', 'USERNAME');
  renameDirectNamedChild(blockElement, 'value', 'CLIENT', 'CLIENT_ID');
}

function normalizeJsonReferenceField(
  blockElement: Element,
  fieldName: string,
  aliasNames: string[],
  fallbackType = ''
): void {
  const legacyValue = getDirectNamedChildFromAliases(blockElement, 'value', aliasNames);
  const reference = extractVariableReferenceFromValue(legacyValue, fallbackType)
    || getVariableReferenceFromField(getDirectNamedChildByTag(blockElement, 'field', fieldName), '', fallbackType);

  if (!reference) {
    return;
  }

  if (legacyValue?.parentElement === blockElement) {
    blockElement.removeChild(legacyValue);
  }

  ensureVariableField(blockElement, fieldName, reference);
}

function normalizeLegacyJsonFieldReferenceInputs(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();

  if ([
    'json_object_get',
    'json_object_set_string',
    'json_object_set_number',
    'json_object_set_bool',
    'json_object_set_null',
    'json_object_set_json',
  ].includes(blockType)) {
    normalizeJsonReferenceField(blockElement, 'OBJ', ['OBJECT', 'OBJ', 'JSON'], JSON_NODE_VARIABLE_TYPE);
  }

  if ([
    'json_array_size',
    'json_array_get',
    'json_array_add_string',
    'json_array_add_number',
    'json_array_add_bool',
    'json_array_add_null',
    'json_array_add_json',
    'json_array_remove',
  ].includes(blockType)) {
    normalizeJsonReferenceField(blockElement, 'ARR', ['ARRAY', 'ARR', 'JSON'], JSON_ARRAY_VARIABLE_TYPE);
  }

  if (['json_delete', 'json_as_string', 'json_as_number', 'json_as_bool'].includes(blockType)) {
    normalizeJsonReferenceField(blockElement, 'JSON', ['OBJECT', 'JSON', 'ARRAY', 'ARR']);
  }

  if (blockType === 'json_object_set_json') {
    normalizeJsonReferenceField(blockElement, 'CHILD', ['CHILD', 'VALUE', 'JSON']);
  }

  if (blockType === 'json_array_add_json') {
    normalizeJsonReferenceField(blockElement, 'CHILD', ['CHILD', 'VALUE', 'JSON']);
  }
}

function normalizeLegacyJsonCreateVarValue(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (blockType !== 'json_create_object_var' && blockType !== 'json_create_array_var') {
    return;
  }

  const parentValue = blockElement.parentElement;
  if (getElementTagName(parentValue) !== 'value') {
    return;
  }

  if (((parentValue?.getAttribute('name') || '').trim()) !== 'VALUE') {
    return;
  }

  const assignmentBlock = parentValue?.parentElement;
  if (!assignmentBlock || getElementTagName(assignmentBlock) !== 'block') {
    return;
  }

  const assignmentType = (assignmentBlock.getAttribute('type') || '').trim().toLowerCase();
  if (assignmentType !== 'variables_set') {
    return;
  }

  const isObject = blockType === 'json_create_object_var';
  blockElement.setAttribute('type', isObject ? 'json_create_object' : 'json_create_array');

  const legacyVarField = getDirectNamedChildByTag(blockElement, 'field', isObject ? 'OBJ_VAR' : 'ARR_VAR');
  if (legacyVarField) {
    blockElement.removeChild(legacyVarField);
  }

  const variableType = isObject ? JSON_NODE_VARIABLE_TYPE : JSON_ARRAY_VARIABLE_TYPE;
  const variableReference = getVariableReferenceFromField(
    getDirectNamedChildByTag(assignmentBlock, 'field', 'VAR'),
    isObject ? 'json_object_1' : 'json_array_1',
    variableType
  );

  if (variableReference) {
    ensureVariableField(assignmentBlock, 'VAR', {
      ...variableReference,
      variableType: variableReference.variableType || variableType,
    });
  }
}

function normalizeJsonLegacyStructure(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (blockType === 'json_stringify_object') {
    renameDirectNamedChild(blockElement, 'value', 'OBJECT', 'VALUE');
    renameDirectNamedChild(blockElement, 'value', 'JSON', 'VALUE');

    if (isStatementLikeBlockContext(blockElement)) {
      blockElement.setAttribute('type', 'variables_set');
      ensureVariableField(blockElement, 'VAR', inferLegacyJsonStringifyResultReference(blockElement));
    }

    return;
  }

  if (blockType !== 'json_create_object') {
    return;
  }

  const assignmentContext = getLegacyJsonAssignmentContext(blockElement);
  const objectReference = inferLegacyJsonObjectReference(blockElement, assignmentContext);
  const itemIndexes = getLegacyJsonObjectItemIndexes(blockElement);
  const appendedBlocks: Element[] = [];

  itemIndexes.forEach((index) => {
    const keyValue = takeDirectNamedChild(blockElement, 'value', `KEY${index}`);
    const valueValue = takeDirectNamedChild(blockElement, 'value', `VALUE${index}`);
    if (!keyValue) {
      return;
    }

    const setterType = inferLegacyJsonObjectSetterType(valueValue);
    appendedBlocks.push(createJsonObjectSetterBlock(
      setterType,
      objectReference,
      keyValue,
      valueValue
    ));
  });

  getLegacyJsonStatementFieldBlocks(blockElement).forEach((fieldBlock) => {
    const setterBlock = createLegacyJsonSetterBlockFromFieldBlock(fieldBlock, objectReference);
    if (setterBlock) {
      appendedBlocks.push(setterBlock);
    }
  });

  getDirectChildElementsByTag(blockElement, 'value')
    .filter((valueElement) => /^(KEY|VALUE)\d+$/.test((valueElement.getAttribute('name') || '').trim()))
    .forEach((valueElement) => {
      blockElement.removeChild(valueElement);
    });

  const mutationElement = getFirstDirectChildByTag(blockElement, 'mutation');
  if (mutationElement) {
    blockElement.removeChild(mutationElement);
  }

  const fieldsStatement = getDirectNamedChildByTag(blockElement, 'statement', 'FIELDS');
  if (fieldsStatement) {
    blockElement.removeChild(fieldsStatement);
  }

  if (!assignmentContext && (appendedBlocks.length > 0 || isStatementLikeBlockContext(blockElement))) {
    blockElement.setAttribute('type', 'json_create_object_var');
    ensureVariableField(blockElement, 'OBJ_VAR', objectReference);
  }

  if (assignmentContext) {
    appendBlocksAfter(assignmentContext.assignmentBlock, appendedBlocks);
  } else {
    appendBlocksAfter(blockElement, appendedBlocks);
  }
}

function createUiObjectFieldSetterBlock(
  setterType: string,
  objectReference: VariableReference,
  valueElements: Element[]
): Element {
  const blockElement = document.createElement('block');
  blockElement.setAttribute('type', setterType);

  ensureVariableField(blockElement, 'OBJ_VAR', objectReference);
  valueElements.forEach((valueElement) => blockElement.appendChild(valueElement));
  return blockElement;
}

function createUiObjectHandleSetterBlock(
  setterType: string,
  objectReference: VariableReference,
  valueElement: Element,
  inputName: string
): Element {
  const blockElement = document.createElement('block');
  blockElement.setAttribute('type', setterType);

  const handleValue = document.createElement('value');
  handleValue.setAttribute('name', 'HANDLE');
  handleValue.appendChild(createVariablesGetShadow(objectReference));
  blockElement.appendChild(handleValue);

  valueElement.setAttribute('name', inputName);
  blockElement.appendChild(valueElement);
  return blockElement;
}

interface BlockConnectionProfile {
  hasPrevious: boolean;
  hasNext: boolean;
  hasOutput: boolean;
}

interface BlockPosition {
  x: number;
  y: number;
}

const ROOT_BLOCK_CHAIN_X_TOLERANCE = 96;
const ROOT_BLOCK_CHAIN_Y_BACKTRACK_TOLERANCE = 8;

const BLOCK_CONNECTION_PROFILE_CACHE = new Map<string, BlockConnectionProfile | null>();
const BLOCK_OUTPUT_CHECK_CACHE = new Map<string, string[] | null>();

function getBlockConnectionProfile(blockType: string): BlockConnectionProfile | null {
  const normalizedType = blockType.trim();
  if (!normalizedType) return null;

  if (BLOCK_CONNECTION_PROFILE_CACHE.has(normalizedType)) {
    return BLOCK_CONNECTION_PROFILE_CACHE.get(normalizedType) || null;
  }

  if (!Blockly.Blocks[normalizedType]) {
    BLOCK_CONNECTION_PROFILE_CACHE.set(normalizedType, null);
    return null;
  }

  const workspace = new Blockly.Workspace();
  try {
    const block = workspace.newBlock(normalizedType);
    const profile: BlockConnectionProfile = {
      hasPrevious: Boolean(block.previousConnection),
      hasNext: Boolean(block.nextConnection),
      hasOutput: Boolean(block.outputConnection),
    };
    BLOCK_CONNECTION_PROFILE_CACHE.set(normalizedType, profile);
    return profile;
  } catch {
    BLOCK_CONNECTION_PROFILE_CACHE.set(normalizedType, null);
    return null;
  } finally {
    workspace.dispose();
  }
}

function getBlockOutputChecks(blockType: string): string[] | null {
  const normalizedType = blockType.trim();
  if (!normalizedType) return null;

  if (BLOCK_OUTPUT_CHECK_CACHE.has(normalizedType)) {
    return BLOCK_OUTPUT_CHECK_CACHE.get(normalizedType) || null;
  }

  if (!Blockly.Blocks[normalizedType]) {
    BLOCK_OUTPUT_CHECK_CACHE.set(normalizedType, null);
    return null;
  }

  const workspace = new Blockly.Workspace();
  try {
    const block = workspace.newBlock(normalizedType);
    const checks = block.outputConnection?.getCheck() || [];
    BLOCK_OUTPUT_CHECK_CACHE.set(normalizedType, checks);
    return checks;
  } catch {
    BLOCK_OUTPUT_CHECK_CACHE.set(normalizedType, null);
    return null;
  } finally {
    workspace.dispose();
  }
}

function getDirectNextBlock(parent: Element): Element | null {
  const nextEl = getFirstDirectChildByTag(parent, 'next');
  if (!nextEl) return null;
  return getFirstDirectChildByTag(nextEl, 'block');
}

function setDirectNextBlock(parent: Element, nextBlock: Element | null): void {
  const existingNext = getFirstDirectChildByTag(parent, 'next');

  if (!nextBlock) {
    if (existingNext) {
      parent.removeChild(existingNext);
    }
    return;
  }

  const nextEl = existingNext || document.createElement('next');
  if (!existingNext) {
    parent.appendChild(nextEl);
  }

  while (nextEl.firstChild) {
    nextEl.removeChild(nextEl.firstChild);
  }

  nextEl.appendChild(nextBlock);
}

function parseBlockCoordinate(blockElement: Element, attributeName: 'x' | 'y'): number | null {
  const rawValue = (blockElement.getAttribute(attributeName) || '').trim();
  if (!rawValue) {
    return null;
  }

  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : null;
}

function getExplicitBlockPosition(blockElement: Element): BlockPosition | null {
  const x = parseBlockCoordinate(blockElement, 'x');
  const y = parseBlockCoordinate(blockElement, 'y');

  if (x === null || y === null) {
    return null;
  }

  return { x, y };
}

function shouldChainSequentialRootElements(previousBlock: Element | null, nextBlock: Element): boolean {
  if (!previousBlock) {
    return true;
  }

  const previousPosition = getExplicitBlockPosition(previousBlock);
  const nextPosition = getExplicitBlockPosition(nextBlock);

  if (!previousPosition || !nextPosition) {
    return true;
  }

  if (nextPosition.y < previousPosition.y - ROOT_BLOCK_CHAIN_Y_BACKTRACK_TOLERANCE) {
    return false;
  }

  return Math.abs(nextPosition.x - previousPosition.x) <= ROOT_BLOCK_CHAIN_X_TOLERANCE;
}

function findFirstStatementBlockInNextChain(startBlock: Element): Element | null {
  let cursor: Element | null = startBlock;
  const visited = new Set<Element>();

  while (cursor && !visited.has(cursor)) {
    visited.add(cursor);

    const blockType = (cursor.getAttribute('type') || '').trim();
    const profile = getBlockConnectionProfile(blockType);

    if (!profile || profile.hasPrevious) {
      return cursor;
    }

    cursor = getDirectNextBlock(cursor);
  }

  return null;
}

function normalizeInvalidNextStatementChain(blockElement: Element): void {
  const nextBlock = getDirectNextBlock(blockElement);
  if (!nextBlock) return;

  const nextType = (nextBlock.getAttribute('type') || '').trim();
  const nextProfile = getBlockConnectionProfile(nextType);

  if (!nextProfile || nextProfile.hasPrevious) {
    return;
  }

  const compatibleStatement = findFirstStatementBlockInNextChain(nextBlock);
  if (compatibleStatement && compatibleStatement !== nextBlock) {
    setDirectNextBlock(blockElement, compatibleStatement);
    return;
  }

  setDirectNextBlock(blockElement, null);
}

function normalizeInvalidStatementRootBlock(blockElement: Element): void {
  const parent = blockElement.parentElement;
  if (!parent) return;

  const parentTag = getElementTagName(parent);
  if (parentTag !== 'statement' && parentTag !== 'next') {
    return;
  }

  const blockType = (blockElement.getAttribute('type') || '').trim();
  const profile = getBlockConnectionProfile(blockType);
  if (!profile || profile.hasPrevious) {
    return;
  }

  const nextBlock = getDirectNextBlock(blockElement);
  const compatibleStatement = nextBlock
    ? findFirstStatementBlockInNextChain(nextBlock)
    : null;
  if (!compatibleStatement) {
    parent.removeChild(blockElement);
    return;
  }

  const replacementParent = compatibleStatement.parentElement;
  replacementParent?.removeChild(compatibleStatement);
  parent.replaceChild(compatibleStatement, blockElement);
}

function normalizeBlockWithoutNextConnection(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim();
  const profile = getBlockConnectionProfile(blockType);
  if (!profile || profile.hasNext) {
    return;
  }

  const nextBlock = getDirectNextBlock(blockElement);
  if (!nextBlock) {
    return;
  }

  const parentTag = getElementTagName(blockElement.parentElement);
  if ((parentTag === 'statement' || parentTag === 'next') && !profile.hasPrevious) {
    return;
  }

  setDirectNextBlock(blockElement, null);
}

const LEGACY_K3_START_BLOCK_TYPES = new Set([
  'ym_k3_when_black_pressed',
  'ym_k3_when_red_pressed',
  'ym_k3_when_green_pressed',
  'ym_5g_on_subscribe_message',
]);

function migrateLegacyK3EventBody(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim();
  if (!LEGACY_K3_START_BLOCK_TYPES.has(blockType)) {
    return;
  }

  const legacyHandle = getDirectNamedChildByTag(blockElement, 'value', 'HANDLE');
  if (legacyHandle && blockType !== 'ym_5g_on_subscribe_message') {
    blockElement.removeChild(legacyHandle);
  }

  const legacyBody = getDirectNamedChildByTag(blockElement, 'statement', 'DO');
  if (!legacyBody) {
    return;
  }

  const bodyBlock = getFirstDirectChildByTag(legacyBody, 'block');
  const existingNextBlock = getDirectNextBlock(blockElement);
  if (bodyBlock) {
    legacyBody.removeChild(bodyBlock);
  }
  blockElement.removeChild(legacyBody);

  if (!bodyBlock) {
    return;
  }

  if (existingNextBlock) {
    existingNextBlock.parentElement?.removeChild(existingNextBlock);
  }
  setDirectNextBlock(blockElement, bodyBlock);

  if (existingNextBlock) {
    setDirectNextBlock(getTailBlock(bodyBlock), existingNextBlock);
  }
}

function upsertDirectField(parent: Element, name: string, value: string): void {
  const existing = getDirectNamedChildByTag(parent, 'field', name);
  if (existing) {
    existing.textContent = value;
    return;
  }

  const fieldEl = document.createElement('field');
  fieldEl.setAttribute('name', name);
  fieldEl.textContent = value;

  const nextEl = getFirstDirectChildByTag(parent, 'next');
  if (nextEl) {
    parent.insertBefore(fieldEl, nextEl);
  } else {
    parent.appendChild(fieldEl);
  }
}

function renameDirectNamedChild(parent: Element, tagName: string, fromName: string, toName: string): void {
  const source = getDirectNamedChildByTag(parent, tagName, fromName);
  if (!source) return;

  const target = getDirectNamedChildByTag(parent, tagName, toName);
  if (target) {
    parent.removeChild(source);
    return;
  }

  source.setAttribute('name', toName);
}

function normalizeUiCreateBlockFields(blockElement: Element): VariableReference | null {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  const uiKind = UI_CREATE_BLOCK_KINDS[blockType];
  if (!uiKind) return null;

  renameDirectNamedChild(blockElement, 'field', 'NAME', 'OBJ_VAR');
  renameDirectNamedChild(blockElement, 'field', 'OBJECT_NAME', 'OBJ_VAR');

  const objectReference = getVariableReferenceFromField(
    getDirectNamedChildByTag(blockElement, 'field', 'OBJ_VAR'),
    getUiDefaultVariableName(uiKind),
    getUiVariableType(uiKind)
  );

  if (objectReference) {
    ensureVariableField(blockElement, 'OBJ_VAR', objectReference);
  }

  if (blockType !== 'yc_ui_screen_create') {
    renameDirectNamedChild(blockElement, 'field', 'PARENT', 'PARENT_VAR');
    renameDirectNamedChild(blockElement, 'field', 'SCREEN', 'PARENT_VAR');

    const legacyParentValue = takeDirectNamedChildFromAliases(blockElement, 'value', ['PARENT', 'SCREEN', 'PARENT_VAR']);
    const parentReference = extractVariableReferenceFromValue(legacyParentValue)
      || getVariableReferenceFromField(getDirectNamedChildByTag(blockElement, 'field', 'PARENT_VAR'));

    if (parentReference) {
      ensureVariableField(blockElement, 'PARENT_VAR', parentReference);
    }
  }

  if (blockType === 'yc_ui_label_create') {
    renameDirectNamedChild(blockElement, 'value', 'LABEL', 'TEXT');
    renameDirectNamedChild(blockElement, 'value', 'CONTENT', 'TEXT');
    renameDirectNamedChild(blockElement, 'value', 'VALUE', 'TEXT');
  }

  return objectReference;
}

function normalizeUiCreateInlineProperties(blockElement: Element, objectReference: VariableReference): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  const appendedBlocks: Element[] = [];

  const inlineSetters = UI_CREATE_INLINE_SETTERS[blockType] || [];
  inlineSetters.forEach(({ legacyValueNames, setterType, setterInputName }) => {
    const movedValue = takeDirectNamedChildFromAliases(blockElement, 'value', legacyValueNames);
    if (!movedValue) return;
    appendedBlocks.push(createUiObjectHandleSetterBlock(setterType, objectReference, movedValue, setterInputName));
  });

  const xValue = takeDirectNamedChildFromAliases(blockElement, 'value', ['X']);
  const yValue = takeDirectNamedChildFromAliases(blockElement, 'value', ['Y']);
  if (xValue) {
    xValue.setAttribute('name', 'X');
  }
  if (yValue) {
    yValue.setAttribute('name', 'Y');
  }
  if (xValue || yValue) {
    appendedBlocks.push(createUiObjectFieldSetterBlock(
      'yc_ui_label_set_pos',
      objectReference,
      [xValue, yValue].filter((value): value is Element => Boolean(value))
    ));
  }

  const widthValue = takeDirectNamedChildFromAliases(blockElement, 'value', ['WIDTH', 'W']);
  const heightValue = takeDirectNamedChildFromAliases(blockElement, 'value', ['HEIGHT', 'H']);
  if (widthValue) {
    widthValue.setAttribute('name', 'WIDTH');
  }
  if (heightValue) {
    heightValue.setAttribute('name', 'HEIGHT');
  }

  if (widthValue && heightValue) {
    appendedBlocks.push(createUiObjectFieldSetterBlock(
      'yc_ui_label_set_size',
      objectReference,
      [widthValue, heightValue]
    ));
  } else if (widthValue) {
    appendedBlocks.push(createUiObjectFieldSetterBlock(
      'yc_ui_label_set_width',
      objectReference,
      [widthValue]
    ));
  } else if (heightValue) {
    appendedBlocks.push(createUiObjectFieldSetterBlock(
      'yc_ui_label_set_height',
      objectReference,
      [heightValue]
    ));
  }

  appendBlocksAfter(blockElement, appendedBlocks);
}

function normalizeUiLegacyStructure(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();

  const fieldHandleKind = UI_FIELD_HANDLE_BLOCKS[blockType];
  if (fieldHandleKind) {
    renameDirectNamedChild(blockElement, 'field', 'HANDLE_VAR', 'OBJ_VAR');

    const legacyHandleValue = takeDirectNamedChild(blockElement, 'value', 'HANDLE');
    const objectReference = extractVariableReferenceFromValue(legacyHandleValue, getUiVariableType(fieldHandleKind))
      || getVariableReferenceFromField(
        getDirectNamedChildByTag(blockElement, 'field', 'OBJ_VAR'),
        '',
        getUiVariableType(fieldHandleKind)
      );

    if (objectReference) {
      ensureVariableField(blockElement, 'OBJ_VAR', objectReference);
    }

    return;
  }

  if (blockType === 'yc_ui_set_active_screen') {
    renameDirectNamedChild(blockElement, 'value', 'SCREEN', 'HANDLE');

    const handleField = getDirectNamedChildByTag(blockElement, 'field', 'SCREEN_VAR')
      || getDirectNamedChildByTag(blockElement, 'field', 'HANDLE_VAR');
    const existingHandleValue = getDirectNamedChildByTag(blockElement, 'value', 'HANDLE');

    if (handleField) {
      const handleReference = getVariableReferenceFromField(handleField, 'ui_screen_1', getUiVariableType('screen'));
      blockElement.removeChild(handleField);

      if (handleReference && !existingHandleValue) {
        const handleValue = document.createElement('value');
        handleValue.setAttribute('name', 'HANDLE');
        handleValue.appendChild(createVariablesGetShadow(handleReference));

        const nextEl = getFirstDirectChildByTag(blockElement, 'next');
        if (nextEl) {
          blockElement.insertBefore(handleValue, nextEl);
        } else {
          blockElement.appendChild(handleValue);
        }
      }
    }
    return;
  }

  const objectReference = normalizeUiCreateBlockFields(blockElement);
  if (!objectReference) return;

  normalizeUiCreateInlineProperties(blockElement, objectReference);
}

function normalizeWirelessPeripheralFields(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (!blockType.startsWith('wireless_peripheral_')) return;

  renameDirectNamedChild(blockElement, 'field', 'PERIPHERAL_TYPE', 'PERIPHERAL');
  renameDirectNamedChild(blockElement, 'field', 'PERIPHERAL_ID', 'PERIPHERAL');

  const peripheral = (getDirectNamedChildByTag(blockElement, 'field', 'PERIPHERAL')?.textContent || '').trim();
  if (!peripheral) {
    const deviceId = (getDirectNamedChildByTag(blockElement, 'field', 'DEVICE_ID')?.textContent || '').trim();
    const inferredPeripheralId = inferPeripheralIdFromDeviceId(deviceId);
    if (inferredPeripheralId) {
      upsertDirectField(blockElement, 'PERIPHERAL', inferredPeripheralId);
    }
  }
}

function normalizeWirelessConnectStructure(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (blockType !== 'wireless_peripheral_connect') return;

  // Legacy XML may use value inputs for device suffix; current block expects a DEVICE_ID field.
  const legacyDeviceIdValue = takeDirectNamedChildFromAliases(blockElement, 'value', ['PERIPHERAL_ID', 'DEVICE_ID']);
  if (legacyDeviceIdValue) {
    const rawDeviceId = (
      legacyDeviceIdValue.querySelector('field[name="TEXT"]')?.textContent
      || legacyDeviceIdValue.querySelector('field[name="NUM"]')?.textContent
      || ''
    )
      .replace(/[^0-9a-zA-Z]/g, '')
      .slice(0, 6);
    upsertDirectField(blockElement, 'DEVICE_ID', rawDeviceId);
  }

  // wireless_peripheral_connect is an output block, so <next> is invalid.
  const nextEl = getFirstDirectChildByTag(blockElement, 'next');
  if (!nextEl) return;

  const chainedBlock = getFirstDirectChildByTag(nextEl, 'block');
  if (!chainedBlock) {
    blockElement.removeChild(nextEl);
    return;
  }

  nextEl.removeChild(chainedBlock);
  blockElement.removeChild(nextEl);

  const parent = blockElement.parentElement;
  if (!parent) {
    return;
  }

  const parentTag = getElementTagName(parent);
  if (parentTag !== 'xml' && parentTag !== 'statement' && parentTag !== 'next') {
    return;
  }

  parent.replaceChild(chainedBlock, blockElement);
  normalizeWirelessPeripheralFields(chainedBlock);

  const chainedType = (chainedBlock.getAttribute('type') || '').trim().toLowerCase();
  const peripheral = (getDirectNamedChildByTag(blockElement, 'field', 'PERIPHERAL')?.textContent || '').trim();
  if (peripheral && chainedType.startsWith('wireless_peripheral_')) {
    const chainedPeripheralField = getDirectNamedChildByTag(chainedBlock, 'field', 'PERIPHERAL');
    if (!chainedPeripheralField || !(chainedPeripheralField.textContent || '').trim()) {
      upsertDirectField(chainedBlock, 'PERIPHERAL', peripheral);
    }
  }

  if (chainedType === 'wireless_peripheral_set_object' && !getDirectNamedChildByTag(chainedBlock, 'value', 'HANDLE')) {
    const handleValue = document.createElement('value');
    handleValue.setAttribute('name', 'HANDLE');
    handleValue.appendChild(blockElement);

    const chainedNext = getFirstDirectChildByTag(chainedBlock, 'next');
    if (chainedNext) {
      chainedBlock.insertBefore(handleValue, chainedNext);
    } else {
      chainedBlock.appendChild(handleValue);
    }
    return;
  }

  if (parentTag === 'xml') {
    parent.appendChild(blockElement);
  }
}

function normalizeWirelessSetObjectStructure(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (blockType !== 'wireless_peripheral_set_object') return;

  const handleValue = getDirectNamedChildByTag(blockElement, 'value', 'HANDLE');
  const handleBlock = getFirstDirectChildByTag(handleValue || blockElement, 'block')
    || getFirstDirectChildByTag(handleValue || blockElement, 'shadow');
  const handlePeripheral = (
    getDirectNamedChildByTag(handleBlock || blockElement, 'field', 'PERIPHERAL')?.textContent
    || inferPeripheralIdFromDeviceId(
      getDirectNamedChildByTag(handleBlock || blockElement, 'field', 'DEVICE_ID')?.textContent || ''
    )
    || ''
  ).trim();

  const peripheral = (getDirectNamedChildByTag(blockElement, 'field', 'PERIPHERAL')?.textContent || '').trim()
    || handlePeripheral;
  if (peripheral) {
    upsertDirectField(blockElement, 'PERIPHERAL', peripheral);
  }

  const fallbackType = peripheral ? `PeripheralObject:${peripheral}` : '';

  const legacyObjectValue = takeDirectNamedChildFromAliases(blockElement, 'value', ['OBJECT', 'OBJ_VAR']);
  const objectReference = extractVariableReferenceFromValue(legacyObjectValue, fallbackType)
    || getVariableReferenceFromField(getDirectNamedChildByTag(blockElement, 'field', 'OBJ_VAR'), '', fallbackType);

  if (objectReference) {
    ensureVariableField(blockElement, 'OBJ_VAR', objectReference);
  }
}

function normalizeWirelessDisconnectStructure(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (blockType !== 'wireless_peripheral_disconnect') return;

  const peripheral = (getDirectNamedChildByTag(blockElement, 'field', 'PERIPHERAL')?.textContent || '').trim();
  const legacyHandle = getDirectNamedChildByTag(blockElement, 'value', 'HANDLE');
  if (!legacyHandle) return;

  const fallbackType = peripheral ? `PeripheralObject:${peripheral}` : '';
  const objectReference = extractVariableReferenceFromValue(legacyHandle, fallbackType);
  if (objectReference && !getDirectNamedChildByTag(blockElement, 'field', 'OBJ_VAR')) {
    ensureVariableField(blockElement, 'OBJ_VAR', objectReference);
  }
  blockElement.removeChild(legacyHandle);
}

function promoteNumericValueInputToField(
  blockElement: Element,
  valueInputName: string,
  fieldName: string,
  mapper: (value: number) => string
): void {
  const valueEl = getDirectNamedChildByTag(blockElement, 'value', valueInputName);
  if (!valueEl) return;

  const raw = (valueEl.querySelector('field[name="NUM"]')?.textContent || '').trim();
  const parsed = Number(raw);
  if (!raw || !Number.isFinite(parsed)) return;

  upsertDirectField(blockElement, fieldName, mapper(parsed));
  blockElement.removeChild(valueEl);
}

function promoteNumericFieldToValueInput(
  blockElement: Element,
  fieldNames: string[],
  valueInputName: string
): void {
  if (getDirectNamedChildByTag(blockElement, 'value', valueInputName)) {
    return;
  }

  const legacyField = takeDirectNamedChildFromAliases(blockElement, 'field', fieldNames);
  const rawValue = (legacyField?.textContent || '').trim();
  const parsedValue = Number(rawValue);
  if (!rawValue || !Number.isFinite(parsedValue)) {
    return;
  }

  const valueElement = document.createElement('value');
  valueElement.setAttribute('name', valueInputName);
  valueElement.appendChild(createLiteralShadowBlock(document, 'math_number', 'NUM', rawValue));

  const nextElement = getFirstDirectChildByTag(blockElement, 'next');
  if (nextElement) {
    blockElement.insertBefore(valueElement, nextElement);
  } else {
    blockElement.appendChild(valueElement);
  }
}

function normalizeYmLegacyEnumInputs(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();

  if (blockType === 'ym_beep_set_frequency') {
    promoteNumericFieldToValueInput(blockElement, ['FREQUENCY', 'FREQ', 'HZ'], 'FREQUENCY');
  }

  if (blockType === 'ym_led_set_power' || blockType === 'ym_beep_set_power' || blockType === 'ym_swusb_set_power') {
    renameDirectNamedChild(blockElement, 'field', 'POWER', 'STATE');
    promoteNumericValueInputToField(blockElement, 'POWER', 'STATE', (value) => (value > 0 ? '1' : '0'));
  }
}

function migrateLegacyYmLedFlashDuration(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (blockType !== 'ym_led_set_flash_duration') return;

  blockElement.setAttribute('type', 'ym_led_flash_once');
}

function normalizeScratchWaitInputs(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (blockType !== 'scratch_control_wait') return;

  // Legacy XML may output DURATION/SECONDS; current block expects SECS.
  renameDirectNamedChild(blockElement, 'value', 'DURATION', 'SECS');
  renameDirectNamedChild(blockElement, 'value', 'SECONDS', 'SECS');
  renameDirectNamedChild(blockElement, 'value', 'TIME', 'SECS');

  const existingSecsValue = getDirectNamedChildByTag(blockElement, 'value', 'SECS');
  const legacySecsField = takeDirectNamedChildFromAliases(blockElement, 'field', ['SECS', 'DURATION', 'SECONDS', 'TIME']);
  if (!legacySecsField || existingSecsValue) {
    return;
  }

  const rawSecs = (legacySecsField.textContent || '').trim() || '1';
  const secsValue = document.createElement('value');
  secsValue.setAttribute('name', 'SECS');

  const secsShadow = document.createElement('shadow');
  secsShadow.setAttribute('type', 'math_number');

  const secsField = document.createElement('field');
  secsField.setAttribute('name', 'NUM');
  secsField.textContent = rawSecs;

  secsShadow.appendChild(secsField);
  secsValue.appendChild(secsShadow);

  const nextEl = getFirstDirectChildByTag(blockElement, 'next');
  if (nextEl) {
    blockElement.insertBefore(secsValue, nextEl);
  } else {
    blockElement.appendChild(secsValue);
  }
}

function formatNumberDigitsValue(value: number): string {
  const normalized = Math.max(0, Math.min(20, Math.trunc(value)));
  return String(normalized);
}

function extractNumericLiteralFromValue(valueElement: Element | null): number | null {
  if (!valueElement) return null;

  const raw = (
    valueElement.querySelector('field[name="NUM"]')?.textContent
    || valueElement.querySelector('field[name="TEXT"]')?.textContent
    || ''
  ).trim();
  if (!raw) return null;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeTextNumberFormatStructure(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (blockType !== 'text_number_to_string_custom') return;

  renameDirectNamedChild(blockElement, 'value', 'NUM', 'VALUE');
  renameDirectNamedChild(blockElement, 'value', 'NUMBER', 'VALUE');
  renameDirectNamedChild(blockElement, 'field', 'DECIMALS', 'DIGITS');
  renameDirectNamedChild(blockElement, 'field', 'PRECISION', 'DIGITS');

  const digitsField = getDirectNamedChildByTag(blockElement, 'field', 'DIGITS');
  const rawFieldDigits = (digitsField?.textContent || '').trim();
  if (rawFieldDigits) {
    const parsedDigits = Number(rawFieldDigits);
    if (Number.isFinite(parsedDigits)) {
      digitsField!.textContent = formatNumberDigitsValue(parsedDigits);
    }
  }

  const legacyDigitsValue = takeDirectNamedChildFromAliases(blockElement, 'value', ['DECIMALS', 'DIGITS', 'PRECISION']);
  const extractedDigits = extractNumericLiteralFromValue(legacyDigitsValue);
  if (extractedDigits !== null) {
    upsertDirectField(blockElement, 'DIGITS', formatNumberDigitsValue(extractedDigits));
    return;
  }

  if (!getDirectNamedChildByTag(blockElement, 'field', 'DIGITS')) {
    upsertDirectField(blockElement, 'DIGITS', '6');
  }
}

function ensureYmHandleInputFromLegacyField(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (!blockType.startsWith('ym_')) return;

  const handleField = getDirectNamedChildByTag(blockElement, 'field', 'HANDLE_VAR');
  if (!handleField) return;

  const variableName = (handleField.textContent || '').trim();
  const variableId = (handleField.getAttribute('id') || '').trim();
  const variableType = (handleField.getAttribute('variabletype') || handleField.getAttribute('variableType') || '').trim();
  blockElement.removeChild(handleField);

  if (!variableName) return;

  const existingHandleValue = getDirectNamedChildByTag(blockElement, 'value', 'HANDLE');
  if (existingHandleValue) return;

  const valueEl = document.createElement('value');
  valueEl.setAttribute('name', 'HANDLE');

  const shadowEl = document.createElement('shadow');
  shadowEl.setAttribute('type', 'variables_get');

  const fieldEl = document.createElement('field');
  fieldEl.setAttribute('name', 'VAR');
  if (variableId) {
    fieldEl.setAttribute('id', variableId);
  }
  if (variableType) {
    fieldEl.setAttribute('variabletype', variableType);
  }
  fieldEl.textContent = variableName;

  shadowEl.appendChild(fieldEl);
  valueEl.appendChild(shadowEl);

  const nextEl = getDirectChildElementsByTag(blockElement, 'next')[0] || null;
  if (nextEl) {
    blockElement.insertBefore(valueEl, nextEl);
  } else {
    blockElement.appendChild(valueEl);
  }
}

function removeYmLegacyResultInput(blockElement: Element): void {
  const blockType = (blockElement.getAttribute('type') || '').trim().toLowerCase();
  if (!blockType.startsWith('ym_')) return;

  const legacyResultValue = getDirectNamedChildByTag(blockElement, 'value', 'RESULT_VAR');
  if (legacyResultValue) {
    blockElement.removeChild(legacyResultValue);
  }

  const legacyResultStatement = getDirectNamedChildByTag(blockElement, 'statement', 'RESULT_VAR');
  if (legacyResultStatement) {
    blockElement.removeChild(legacyResultStatement);
  }
}

function normalizeWithHeadlessWorkspace(xmlDom: Element): Element {
  const headlessWorkspace = new Blockly.Workspace();
  try {
    Blockly.Xml.domToWorkspace(xmlDom, headlessWorkspace);
    return Blockly.Xml.workspaceToDom(headlessWorkspace) as Element;
  } finally {
    headlessWorkspace.dispose();
  }
}

export function normalizeWorkspaceImportXml(xmlText: string, workspace?: Blockly.Workspace): {
  xmlDom: Element;
  unknownBlockTypes: string[];
  warnings: string[];
} {
  const rawText = stripMarkdownCodeFence(xmlText);
  if (!rawText) {
    throw new Error('XML 内容为空。');
  }

  const wrappedText = ensureXmlRoot(rawText);
  const xmlDom = Blockly.utils.xml.textToDom(wrappedText) as Element;
  normalizeValueLiteralShorthand(xmlDom);
  normalizeDetachedRootNextElements(xmlDom);
  normalizeSequentialStatementChildren(xmlDom);
  const blockNodes = collectTypedNodes(xmlDom);

  blockNodes.forEach((node) => {
    const originalType = (node.getAttribute('type') || '').trim().replace(/^["']+|["']+$/g, '');
    if (!originalType) return;
    if (node.getAttribute('type') !== originalType) {
      node.setAttribute('type', originalType);
    }
    const mappedType = BLOCK_TYPE_ALIASES[originalType] || BLOCK_TYPE_ALIASES[originalType.toLowerCase()];
    if (mappedType) {
      node.setAttribute('type', mappedType);
    }
  });

  blockNodes.forEach((node) => {
    migrateLegacyK3EventBody(node);
    normalizeScratchWaitInputs(node);
    normalizeTextNumberFormatStructure(node);
    normalizeJsonLegacyStructure(node);
    normalizeLegacyJsonCreateVarValue(node);
    normalizeLegacyJsonFieldReferenceInputs(node);
    normalizeLegacyMqttInitInputs(node);
    normalizeLegacyMqttTopicField(node);
    normalizeWirelessPeripheralFields(node);
    normalizeWirelessConnectStructure(node);
    normalizeWirelessSetObjectStructure(node);
    normalizeWirelessDisconnectStructure(node);
    normalizeUiLegacyStructure(node);
    normalizeUiColorFieldInput(node);
    normalizeControlsIfMutation(node);
    normalizeVariableFieldTypeHints(node);
    normalizeInvalidStatementRootBlock(node);
    normalizeInvalidNextStatementChain(node);
    normalizeBlockWithoutNextConnection(node);
    ensureYmHandleInputFromLegacyField(node);
    normalizeYmLegacyEnumInputs(node);
    migrateLegacyYmLedFlashDuration(node);
    removeYmLegacyResultInput(node);
  });

  ensureYmHandleInputs(xmlDom, workspace);

  normalizeSequentialRootBlocks(xmlDom);

  migrateLegacyPreservedCScriptSource(xmlDom);

  const unknownBlockTypes = Array.from(new Set(
    collectTypedNodes(xmlDom)
      .map((node) => node.getAttribute('type')?.trim() || '')
      .filter((type) => Boolean(type) && !Blockly.Blocks[type])
  ));

  if (unknownBlockTypes.length > 0) {
    return {
      xmlDom,
      unknownBlockTypes,
      warnings: [],
    };
  }

  const warnings = normalizeVariableReferenceIds(xmlDom, workspace);

  const normalizedDom = normalizeWithHeadlessWorkspace(xmlDom);

  return {
    xmlDom: normalizedDom,
    unknownBlockTypes,
    warnings,
  };
}

function unknownTypesMessage(types: string[]): string {
  return `AI 返回了未注册的积木类型：${types.join(', ')}。请使用当前工具箱里已存在的积木类型。`;
}

function toErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }
  return fallback;
}

/** 工作区配置选项 */
export interface WorkspaceOptions {
  /** 是否显示网格 */
  grid?: boolean;
  /** 是否对齐网格 */
  gridSnap?: boolean;
  /** 是否显示缩放控件 */
  zoom?: boolean;
  /** 是否显示垃圾桶 */
  trashcan?: boolean;
  /** 渲染器类型 */
  renderer?: string;
  /** 是否只读 */
  readOnly?: boolean;
  /** 自定义工具箱XML */
  toolboxXml?: string;
}

/** 默认配置 */
const DEFAULT_OPTIONS: WorkspaceOptions = {
  grid: true,
  zoom: true,
  trashcan: true,
  renderer: 'zelos',
  readOnly: false,
};

/**
 * 创建 Blockly 工作区
 * @param container 容器元素
 * @param options 配置选项
 * @returns Blockly 工作区实例
 */
export function createBlocklyWorkspace(
  container: HTMLElement,
  options: WorkspaceOptions = {}
): Blockly.WorkspaceSvg {
  const gridColour =
    document.documentElement.getAttribute('data-theme') === 'dark' ? '#1c2a44' : '#d0d0d0';
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };

  // 注册自定义积木
  registerBlocks();

  // 获取工具箱XML
  const toolboxXml = mergedOptions.toolboxXml ?? getToolboxXml();

  // 创建配置对象
  const blocklyOptions: Blockly.BlocklyOptions = {
    toolbox: toolboxXml,
    renderer: mergedOptions.renderer,
    readOnly: mergedOptions.readOnly,
    trashcan: mergedOptions.trashcan,
    media: '/blockly-media/',
    sounds: true,
    oneBasedIndex: true,
    horizontalLayout: false,
    toolboxPosition: 'start',
    scrollbars: true,
    move: {
      scrollbars: true,
      drag: true,
      wheel: true,
    },
  };

  // 网格配置
  if (mergedOptions.grid) {
    blocklyOptions.grid = {
      spacing: 20,
      length: 3,
      colour: gridColour,
      snap: mergedOptions.gridSnap ?? true,
    };
  }

  // 缩放配置
  if (mergedOptions.zoom) {
    blocklyOptions.zoom = {
      controls: true,
      wheel: true,
      startScale: 1.0,
      maxScale: 3,
      minScale: 0.3,
      scaleSpeed: 1.2,
    };
  }

  // 注入工作区
  const workspace = Blockly.inject(container, blocklyOptions);

  return workspace;
}

/**
 * 序列化工作区为XML字符串
 * @param workspace 工作区实例
 */
export function serializeWorkspace(workspace: Blockly.WorkspaceSvg): string {
  const xml = Blockly.Xml.workspaceToDom(workspace);
  sanitizeRuntimeMqttPasswordInputs(xml as Element);
  return Blockly.Xml.domToText(xml);
}

/** 获取最近一次工作区 XML 导入失败原因 */
export function getLastWorkspaceImportError(): string {
  return lastWorkspaceImportError;
}

export function getLastWorkspaceImportWarning(): string {
  return lastWorkspaceImportWarning;
}

export function layoutCScriptFlows(
  workspace: Blockly.WorkspaceSvg,
  topBlocks: Blockly.BlockSvg[],
  origin = { x: 48, y: 48 },
): void {
  if (topBlocks.length === 0) return;

  topBlocks.forEach((block) => block.render());

  const mainFlows = topBlocks.filter((block) => block.type === 'scratch_event_when_green_flag');
  const procedures = topBlocks.filter((block) => (
    block.type === 'procedures_defnoreturn' || block.type === 'procedures_defreturn'
  ));
  const eventFlows = topBlocks.filter((block) => (
    block.type !== 'scratch_event_when_green_flag'
    && block.type !== 'procedures_defnoreturn'
    && block.type !== 'procedures_defreturn'
    && (block.type.includes('_when_') || block.type === 'ym_5g_on_subscribe_message')
  ));
  const classifiedIds = new Set([...mainFlows, ...procedures, ...eventFlows].map((block) => block.id));
  const otherFlows = topBlocks.filter((block) => !classifiedIds.has(block.id));
  const orderedMainFlows = mainFlows.length > 0 ? mainFlows : otherFlows.splice(0, 1);
  const remainingFlows = [...procedures, ...eventFlows, ...otherFlows];
  const flowGap = 56;
  const columnGap = 96;

  const placeFlow = (block: Blockly.BlockSvg, x: number, y: number) => {
    const bounds = block.getBoundingRectangle();
    block.moveBy(x - bounds.left, y - bounds.top);
    return {
      width: bounds.right - bounds.left,
      height: bounds.bottom - bounds.top,
    };
  };

  let mainBottom = origin.y;
  let mainRight = origin.x;
  orderedMainFlows.forEach((block) => {
    const size = placeFlow(block, origin.x, mainBottom);
    mainRight = Math.max(mainRight, origin.x + size.width);
    mainBottom += size.height + flowGap;
  });
  mainBottom = Math.max(origin.y, mainBottom - flowGap);

  const tallestRemaining = remainingFlows.reduce((height, block) => {
    const bounds = block.getBoundingRectangle();
    return Math.max(height, bounds.bottom - bounds.top);
  }, 0);
  const gridBottom = origin.y + Math.max(mainBottom - origin.y, tallestRemaining);
  let columnX = mainRight + columnGap;
  let columnWidth = 0;
  let nextY = origin.y;

  remainingFlows.forEach((block) => {
    const bounds = block.getBoundingRectangle();
    const width = bounds.right - bounds.left;
    const height = bounds.bottom - bounds.top;
    if (nextY > origin.y && nextY + height > gridBottom) {
      columnX += columnWidth + columnGap;
      columnWidth = 0;
      nextY = origin.y;
    }

    placeFlow(block, columnX, nextY);
    columnWidth = Math.max(columnWidth, width);
    nextY += height + flowGap;
  });

  workspace.resizeContents();
}

function layoutUnpositionedImportedFlows(workspace: Blockly.WorkspaceSvg, xmlDom: Element): void {
  const unpositionedIds = new Set(
    Array.from(xmlDom.children)
      .filter((element) => (
        element.tagName.toLowerCase() === 'block'
        && !element.hasAttribute('x')
        && !element.hasAttribute('y')
      ))
      .map((element) => element.getAttribute('id'))
      .filter((id): id is string => Boolean(id))
  );
  const topBlocks = workspace.getTopBlocks(false)
    .filter((block): block is Blockly.BlockSvg => unpositionedIds.has(block.id));
  layoutCScriptFlows(workspace, topBlocks);
}

function isCScriptImport(xmlDom: Element): boolean {
  return Array.from(xmlDom.getElementsByTagName('data')).some((data) => (
    data.textContent?.startsWith('cscript-preserved:')
  ));
}

/**
 * 从XML字符串恢复工作区
 * @param workspace 工作区实例
 * @param xmlText XML字符串
 */
export function deserializeWorkspace(
  workspace: Blockly.WorkspaceSvg,
  xmlText: string
): boolean {
  clearWorkspaceImportError();
  try {
    const { xmlDom, unknownBlockTypes, warnings } = normalizeWorkspaceImportXml(xmlText, workspace);
    if (unknownBlockTypes.length > 0) {
      const msg = unknownTypesMessage(unknownBlockTypes);
      setWorkspaceImportError(msg);
      console.error('Failed to deserialize workspace:', msg);
      return false;
    }

    if (warnings.length > 0) {
      setWorkspaceImportWarning(Array.from(new Set(warnings)).join('；'));
    }

    workspace.clear();
    Blockly.Xml.domToWorkspace(xmlDom, workspace);
    if (!isCScriptImport(xmlDom)) {
      layoutUnpositionedImportedFlows(workspace, xmlDom);
    }
    return true;
  } catch (error) {
    const message = toErrorMessage(error, '工作区导入失败。');
    setWorkspaceImportError(message);
    console.error('Failed to deserialize workspace:', error);
    return false;
  }
}

/**
 * 追加 XML 积木到工作区（不清空已有内容）
 * @param workspace 工作区实例
 * @param xmlText XML字符串
 */
export function appendWorkspaceBlocks(
  workspace: Blockly.WorkspaceSvg,
  xmlText: string
): boolean {
  clearWorkspaceImportError();
  try {
    const beforeVariableCount = workspace.getAllVariables().length;
    const existingBlockIds = new Set(workspace.getAllBlocks(false).map((block) => block.id));
    const { xmlDom, unknownBlockTypes, warnings } = normalizeWorkspaceImportXml(xmlText, workspace);
    if (unknownBlockTypes.length > 0) {
      const msg = unknownTypesMessage(unknownBlockTypes);
      setWorkspaceImportError(msg);
      console.error('Failed to append workspace blocks:', msg);
      return false;
    }

    if (warnings.length > 0) {
      setWorkspaceImportWarning(Array.from(new Set(warnings)).join('；'));
    }

    Blockly.Xml.domToWorkspace(xmlDom, workspace);

    const allBlocksAfterImport = workspace.getAllBlocks(false);
    const newBlocks = allBlocksAfterImport.filter((block) => !existingBlockIds.has(block.id));
    const afterVariableCount = workspace.getAllVariables().length;
    const hasVisibleChanges = newBlocks.length > 0 || afterVariableCount > beforeVariableCount;

    if (!hasVisibleChanges) {
      setWorkspaceImportError('未检测到可导入的积木，请检查 AI 返回的 XML 是否包含有效 <block> 节点。');
      return false;
    }

    const existingTopBlocks = workspace
      .getTopBlocks(false)
      .filter((block) => existingBlockIds.has(block.id)) as Blockly.BlockSvg[];

    if (isCScriptImport(xmlDom)) {
      return true;
    }

    const newTopBlocks = workspace
      .getTopBlocks(false)
      .filter((block) => !existingBlockIds.has(block.id)) as Blockly.BlockSvg[];

    const sortByPosition = (a: Blockly.BlockSvg, b: Blockly.BlockSvg): number => {
      const posA = a.getRelativeToSurfaceXY();
      const posB = b.getRelativeToSurfaceXY();
      if (posA.y !== posB.y) return posA.y - posB.y;
      return posA.x - posB.x;
    };

    const shouldChainSequentialTopBlocks = (
      previousHead: Blockly.BlockSvg | null,
      nextHead: Blockly.BlockSvg,
    ): boolean => {
      if (!previousHead) {
        return true;
      }

      const previousPosition = previousHead.getRelativeToSurfaceXY();
      const nextPosition = nextHead.getRelativeToSurfaceXY();

      if (nextPosition.y < previousPosition.y - ROOT_BLOCK_CHAIN_Y_BACKTRACK_TOLERANCE) {
        return false;
      }

      return Math.abs(nextPosition.x - previousPosition.x) <= ROOT_BLOCK_CHAIN_X_TOLERANCE;
    };

    const getChainTailWithOpenNext = (topBlock: Blockly.BlockSvg): Blockly.BlockSvg | null => {
      let current: Blockly.BlockSvg = topBlock;
      while (current.nextConnection?.targetBlock()) {
        current = current.nextConnection.targetBlock() as Blockly.BlockSvg;
      }

      if (!current.nextConnection || current.nextConnection.isConnected()) {
        return null;
      }

      return current;
    };

    const connectChainHeadToTail = (
      tailBlock: Blockly.BlockSvg,
      headBlock: Blockly.BlockSvg,
    ): Blockly.BlockSvg | null => {
      if (!tailBlock.nextConnection || tailBlock.nextConnection.isConnected()) {
        return null;
      }

      if (!headBlock.previousConnection || headBlock.previousConnection.isConnected()) {
        return null;
      }

      try {
        tailBlock.nextConnection.connect(headBlock.previousConnection);
      } catch {
        return null;
      }

      let newTail: Blockly.BlockSvg = headBlock;
      while (newTail.nextConnection?.targetBlock()) {
        newTail = newTail.nextConnection.targetBlock() as Blockly.BlockSvg;
      }

      return newTail.nextConnection && !newTail.nextConnection.isConnected()
        ? newTail
        : null;
    };

    const getBlockBottom = (block: Blockly.BlockSvg): number => {
      const position = block.getRelativeToSurfaceXY();
      const size = block.getHeightWidth();
      return position.y + size.height;
    };

    const tailCandidates = existingTopBlocks
      .map((block) => getChainTailWithOpenNext(block))
      .filter((block): block is Blockly.BlockSvg => Boolean(block))
      .sort(sortByPosition);

    let appendTail: Blockly.BlockSvg | null = tailCandidates.length > 0
      ? tailCandidates[tailCandidates.length - 1]
      : null;
    const sortedNewTopBlocks = [...newTopBlocks].sort(sortByPosition);
    const stitchedTopBlockIds = new Set<string>();

    // Prefer stitching newly generated statement chains to the current flow tail.
    sortedNewTopBlocks.forEach((headBlock) => {
      if (!appendTail || !appendTail.nextConnection || appendTail.nextConnection.isConnected()) {
        return;
      }

      if (!headBlock.previousConnection || headBlock.previousConnection.isConnected()) {
        return;
      }

      const newTail = connectChainHeadToTail(appendTail, headBlock);
      if (!newTail) {
        return;
      }

      stitchedTopBlockIds.add(headBlock.id);
      appendTail = newTail;
    });

    let detachedSequenceAnchor: Blockly.BlockSvg | null = null;
    let detachedSequenceTail: Blockly.BlockSvg | null = null;

    sortedNewTopBlocks.forEach((headBlock) => {
      if (stitchedTopBlockIds.has(headBlock.id)) {
        return;
      }

      if (
        !detachedSequenceTail
        || !shouldChainSequentialTopBlocks(detachedSequenceAnchor, headBlock)
      ) {
        detachedSequenceAnchor = headBlock;
        detachedSequenceTail = getChainTailWithOpenNext(headBlock);
        return;
      }

      const newTail = connectChainHeadToTail(detachedSequenceTail, headBlock);
      if (!newTail) {
        detachedSequenceAnchor = headBlock;
        detachedSequenceTail = getChainTailWithOpenNext(headBlock);
        return;
      }

      stitchedTopBlockIds.add(headBlock.id);
      detachedSequenceAnchor = headBlock;
      detachedSequenceTail = newTail;
    });

    const detachedTopBlocks = sortedNewTopBlocks.filter((block) => !stitchedTopBlockIds.has(block.id));

    if (detachedTopBlocks.length > 0) {
      const detachedIds = new Set(detachedTopBlocks.map((block) => block.id));
      const anchoredTopBlocks = workspace
        .getTopBlocks(false)
        .filter((block) => !detachedIds.has(block.id)) as Blockly.BlockSvg[];

      const referenceBlocks = anchoredTopBlocks.length > 0 ? anchoredTopBlocks : existingTopBlocks;
      const baseX = referenceBlocks.length > 0
        ? Math.round(
          referenceBlocks.reduce((sum, block) => sum + block.getRelativeToSurfaceXY().x, 0)
          / referenceBlocks.length
        )
        : 24;

      let nextY = referenceBlocks.reduce((maxY, block) => {
        return Math.max(maxY, getBlockBottom(block) + 24);
      }, 24);

      detachedTopBlocks.forEach((block) => {
        const position = block.getRelativeToSurfaceXY();
        block.moveBy(baseX - position.x, nextY - position.y);

        const size = block.getHeightWidth();
        nextY += size.height + 24;
      });
    }

    return true;
  } catch (error) {
    const message = toErrorMessage(error, '积木追加失败。');
    setWorkspaceImportError(message);
    console.error('Failed to append workspace blocks:', error);
    return false;
  }
}

/**
 * 居中显示指定块
 * @param workspace 工作区实例
 * @param blockId 块ID
 */
export function focusBlock(workspace: Blockly.WorkspaceSvg, blockId: string): boolean {
  const block = workspace.getBlockById(blockId);
  if (!block) return false;

  // 选中块
  block.select();

  // 居中显示
  workspace.centerOnBlock(blockId);

  return true;
}

export default createBlocklyWorkspace;
